/**
 * RTK Query API slice for HellocashflowAdmin.
 *
 * @remarks
 * This module centralizes:
 * - auth token injection (Firebase ID token via `MemoizedAuth`)
 * - retry/backoff + token refresh on 401
 * - endpoint definitions and cache invalidation tags
 * - small response normalizers/coercers for admin UI types
 *
 * Demo Access note:
 * When `VITE_ADMIN_DEMO_ACCESS_API_MODE=stub`, only the `/admin/demo-access` endpoints are handled via a
 * localStorage-backed stub store. All other endpoints remain live.
 *
 * Note: Network/auth concerns live in `src/store/api/baseQueryWithAuth.ts`.
 */
import { createApi } from "@reduxjs/toolkit/query/react";

import { AdminUserWithClaimsResponse } from "../../HelloCashflowDomain/DomainTypes/api/AdminUserWithClaimsResponse";
import { AdminBulkEmailJobsResponse } from "../../HelloCashflowDomain/DomainTypes/api/AdminBulkEmailJobsResponse";
import { AdminQuickbooksTrialsResponse } from "../../HelloCashflowDomain/DomainTypes/api/AdminQuickbooksTrialsResponse";
import { AdminSpoofSessionsResponse } from "../../HelloCashflowDomain/DomainTypes/api/AdminSpoofSessionsResponse";
import { AuthProvider } from "../../HelloCashflowDomain/DomainTypes/db/AppUser";
import { OrgSubscriptionStatus } from "../../HelloCashflowDomain/DomainTypes/db/OrganisationOptions";
import type { Log } from "../../types/Logs";
import type {
    Organisation,
    OrganisationUsageActivityStatus,
    OrganisationUser,
} from "../../types/organisation";
import { StripeSubscriptionObjectsResponse } from "../../types/stripeSubscriptionObjects";
import {
    CheckoutSessionResponse,
    CreateCheckoutSessionRequest,
    CreatePortalSessionRequest,
    PortalSessionResponse,
    ProductOnboardingRequest,
    ProductWithPrices,
    SetPrimaryPriceRequest,
    SetPrimaryPriceResponse,
    StripeApiResponse,
    StripePrice,
    StripeProduct,
    StripeSubscription,
    SubscriptionStatisticsApiResponse,
} from "../../types/subscription";
import {
    UsageAnalyticsFilters,
    UsageAnalyticsResponse,
} from "../../types/usageAnalytics";
import type { AllUser, FirebaseUser } from "../../types/Users";
import type {
    AdminStatisticsResponse,
    IndustryBreakdownItem,
} from "../../types/adminStatistics";
import type { AdminMetricsResponse } from "../../types/adminMetrics";
import type {
    AdminPartnerDefaultOrganisationResponse,
    AdminPartnerDetail,
    AdminPartnerEntitlementsResponse,
    AdminPartnerSummary,
    AdminPartnerTransferResponse,
    AdminPartnerUnlinkResponse,
} from "../../types/adminPartners";
import type { JsonValue } from "../../types/bugTracker";
import type {
    TranscriptIntakeCancelResponse,
    TranscriptIntakeCreateRequest,
    TranscriptIntakeCreateResponse,
    TranscriptIntakeJobResponse,
    TranscriptIntakeProcessRequest,
    TranscriptIntakeProcessResponse,
} from "../../types/transcriptIntake";
import type {
    AdminSubscriptionAction,
    AdminSubscriptionLocalSnapshot,
    CreateAuditRunResponse,
    ExecuteAdminSubscriptionActionResponse,
    FixSubscriptionMetadataResponse,
    RebuildSnapshotsResponse,
    ReconcileFromStripeResponse,
    StripeDiscountsResponse,
    StripeInvoicePreviewSummary,
    StripeSubscriptionSummary,
    SubscriptionAuditRun,
} from "../../types/adminSubscriptions";
import type {
    AdminV2ApprovalStatus,
    AdminV2ApprovalsResponse,
    AdminV2AuditLogResponse,
    AdminV2CrmBillingResponsibility,
    AdminV2CrmBusinessRecordResponse,
    AdminV2CrmBusinessesListResponse,
    AdminV2CrmFirmClientRelationshipRecordResponse,
    AdminV2CrmFirmClientRelationshipsListResponse,
    AdminV2CrmFirmRecordResponse,
    AdminV2CrmFirmsListResponse,
    AdminV2CrmImportRunResponse,
    AdminV2InAppMessagesResponse,
    AdminV2MessageTemplatePreviewResponse,
    AdminV2MessageTemplateStatus,
    AdminV2MessageTemplatesResponse,
    AdminV2MessagingCategory,
    AdminV2MessagingChannel,
    AdminV2PersonCommunicationPreferencesResponse,
    AdminV2CrmPeopleBulkLifecycleStageResponse,
    AdminV2CrmPeopleListResponse,
    AdminV2CrmPersonRecordResponse,
    AdminV2CrmRelationshipStatus,
    AdminV2CrmTimelineCategory,
    AdminV2CrmTimelineResponse,
    AdminV2SenderIdentitiesResponse,
    AdminV2SenderIdentityCategory,
    AdminV2SenderIdentityVerificationStatus,
    AdminV2SenderReplyRouting,
    AdminV2GrowthFormDetailResponse,
    AdminV2GrowthFormStatus,
    AdminV2GrowthFormSubmissionStatus,
    AdminV2GrowthFormSubmissionsResponse,
    AdminV2GrowthFormsListResponse,
    AdminV2GrowthCampaignMetricsResponse,
    AdminV2GrowthCampaignPreviewResponse,
    AdminV2GrowthCampaignRecordResponse,
    AdminV2GrowthCampaignSendResponse,
    AdminV2GrowthCampaignStatus,
    AdminV2GrowthCampaignsListResponse,
    AdminV2GrowthSegmentPreviewByIdResponse,
    AdminV2GrowthSegmentPreviewByRulesResponse,
    AdminV2GrowthSegmentRecordResponse,
    AdminV2GrowthSegmentRecomputeJobResponse,
    AdminV2GrowthSegmentStatus,
    AdminV2GrowthSegmentsListResponse,
    AdminV2GrowthSequenceDetailResponse,
    AdminV2GrowthSequenceEnrollPersonResponse,
    AdminV2GrowthSequencePersonEnrollmentsResponse,
    AdminV2GrowthSequenceStatus,
    AdminV2GrowthSequenceUnenrollResponse,
    AdminV2GrowthSequencesListResponse,
    AdminV2AutomationDryRunResponse,
    AdminV2AutomationGraphSchema,
    AdminV2AutomationDetailResponse,
    AdminV2AutomationPublishResponse,
    AdminV2AutomationRunStatus,
    AdminV2AutomationRunDetailResponse,
    AdminV2AutomationRunsResponse,
    AdminV2AutomationStatus,
    AdminV2AutomationsListResponse,
    AdminV2GrowthTagBulkJobResponse,
    AdminV2GrowthTagBulkMode,
    AdminV2GrowthTagBulkPreviewResponse,
    AdminV2GrowthTagDeleteResponse,
    AdminV2GrowthTagMergePreviewResponse,
    AdminV2GrowthTagMergeResponse,
    AdminV2GrowthTagRecordResponse,
    AdminV2GrowthTagsListResponse,
    AdminV2HelpArticleDetailResponse,
    AdminV2HelpArticleUpdateResponse,
    AdminV2HelpArticleSearchResponse,
    AdminV2HelpArticleStatus,
    AdminV2HelpArticlesListResponse,
    AdminV2HelpCollectionRecordResponse,
    AdminV2HelpCollectionUpdateResponse,
    AdminV2HelpCollectionsListResponse,
    AdminV2HelpFeedbackListResponse,
    AdminV2HelpFeedbackRecordResponse,
    AdminV2HelpSearchLogsResponse,
    AdminV2JobCancelResponse,
    AdminV2JobResponse,
    AdminV2JobsResponse,
    AdminV2LearningAssignment,
    AdminV2LearningAssignmentsResponse,
    AdminV2LearningAssignmentStatus,
    AdminV2LearningBusinessScoreDashboardResponse,
    AdminV2LearningContentDetail,
    AdminV2LearningContentsResponse,
    AdminV2LearningContentStatus,
    AdminV2LearningContentType,
    AdminV2LearningPathwayDetail,
    AdminV2LearningPathwaysResponse,
    AdminV2LearningPathwayStatus,
    AdminV2LearningPeopleScoreDashboardResponse,
    AdminV2LearningProgress,
    AdminV2LearningProgressResponse,
    AdminV2LearningProgressStatus,
    AdminV2LearningRecomputeScoreResponse,
    AdminV2LearningScoreExplainabilityResponse,
    AdminV2LearningScoreRule,
    AdminV2LearningScoreRuleVersionsResponse,
    AdminV2LearningScoreTimelineResponse,
    AdminV2MeResponse,
    AdminV2IntegrationsResponse,
    AdminV2IntegrationConnectionHealthResponse,
    AdminV2IntegrationConnectionResponse,
    AdminV2IntegrationConnectionStatus,
    AdminV2SalesDealEventsResponse,
    AdminV2SalesDealEventMutationResponse,
    AdminV2SalesDealMutationResponse,
    AdminV2SalesDealsResponse,
    AdminV2SalesLeadScoresResponse,
    AdminV2SalesMemberScoresResponse,
    AdminV2SalesQueueRole,
    AdminV2SalesRecomputeScoresResponse,
    AdminV2SalesReportingSummaryResponse,
    AdminV2SalesScoreBand,
    AdminV2SalesStage,
    AdminV2SalesWorkQueueResponse,
    AdminV2PosthogDashboardsResponse,
    AdminV2EventTaxonomyResponse,
    AdminV2PatchEventTaxonomyResponse,
    AdminV2ProductEventTaxonomyStatus,
    AdminV2OpsDeliverabilityHealthResponse,
    AdminV2OpsIngestFailuresResponse,
    AdminV2OpsSupportBacklogResponse,
    AdminV2OpsWhatNeedsAttentionResponse,
    AdminV2FeatureFlagsResponse,
    AdminV2FeatureFlagUpsertResponse,
    AdminV2EntitlementsResponse,
    AdminV2EntitlementUpsertResponse,
    AdminV2RbacPermissionsResponse,
    AdminV2RbacRolesResponse,
    AdminV2RbacStaffUsersResponse,
    AdminV2SearchResponse,
    AdminV2SupportTicketAssignResponse,
    AdminV2SupportTicketCreateResponse,
    AdminV2SupportTicketDetailResponse,
    AdminV2SupportTicketInternalNoteResponse,
    AdminV2SupportTicketPriority,
    AdminV2SupportTicketReplyResponse,
    AdminV2SupportTicketSource,
    AdminV2SupportTicketStatus,
    AdminV2SupportTicketStatusResponse,
    AdminV2SupportTicketSuggestedArticlesResponse,
    AdminV2SupportTicketsListResponse,
    JsonValue as AdminV2JsonValue,
} from "../../types/adminV2";
import type {
    AdminDocsSearchResponse,
    AdminDocsShareLinksResponse,
    CreateDocsShareLinkRequest,
    CreateDocsShareLinkResponse,
    DocsShareStatusFilter,
    RevokeDocsShareLinkResponse,
} from "../../types/docsShareLinks";

import { enhancedBaseQuery } from "./baseQueryWithAuth";
import { buildAdminV2RevenueEndpoints } from "./endpoints/adminV2RevenueEndpoints";

export type AdminOrganisationUserSummary = OrganisationUser;
export type AdminOrganisationSummary = Organisation;

export type RemoveOrganisationUserRequest = {
    organisationId: string;
    userId: string;
};

export type RemoveOrganisationUserResponse = {
    removedCount: number;
};

type RawLog = Omit<Log, "timestamp"> & { timestamp: string | number | Date };

const coerceNumber = (candidate: unknown, fallback = 0) => {
    if (typeof candidate === "number" && Number.isFinite(candidate)) {
        return candidate;
    }

    if (typeof candidate === "string") {
        const parsed = Number(candidate);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }

    return fallback;
};

// Demo Access Types
export interface DemoAccessAssignment {
    firebaseId: string;
    email: string;
    displayName: string;
    demoAccessEnabled: boolean;
    grantedBy: string | null;
    updatedAt: string | null;
}

export interface GrantDemoAccessRequest {
    firebaseId?: string;
    email?: string;
}

export type GrantDemoAccessResponse = DemoAccessAssignment;

export interface RevokeDemoAccessRequest {
    firebaseId: string;
}

const toAdminUserSummary = (user: AllUser) => ({
    id: user.id,
    firebaseId: user.firebaseId,
    email: user.email ?? "",
    displayName: user.displayName ?? undefined,
    activity_status: user.activity_status,
    lastLoginAt: user.last_login_at,
    loginCount: user.login_count ?? 0,
    signupDate: user.signup_date,
    usageActiveMinutesLast7Days: user.usage_active_minutes_last_7_days ?? 0,
    usageLastActiveDate: user.usage_last_active_date ?? null,
    organisation_name: user.organisation_name ?? "",
    organisation_type: user.organisation_type ?? "",
    subscription_status: Number(user.subscriptionStatus ?? 0),
    authProviders: user.authProviders,
    providerConnections: user.providerConnections,
});

const toOrganisationUser = (candidate: unknown): OrganisationUser | null => {
    if (!candidate || typeof candidate !== "object") {
        return null;
    }

    const raw = candidate as Record<string, unknown>;
    const id = typeof raw.id === "string" ? raw.id : null;
    const firebaseId =
        typeof raw.firebaseId === "string" ? raw.firebaseId : null;

    if (!id || !firebaseId) {
        return null;
    }

    const firebaseUser =
        raw.firebaseUser && typeof raw.firebaseUser === "object"
            ? (raw.firebaseUser as FirebaseUser)
            : undefined;

    return {
        id,
        firebaseId,
        name: typeof raw.name === "string" ? raw.name : null,
        email: typeof raw.email === "string" ? raw.email : null,
        sms: typeof raw.sms === "string" ? raw.sms : null,
        stripeId: typeof raw.stripeId === "string" ? raw.stripeId : null,
        viewedUpdateLog:
            typeof raw.viewedUpdateLog === "boolean"
                ? raw.viewedUpdateLog
                : Boolean(raw.viewedUpdateLog),
        displayName:
            typeof raw.displayName === "string" ? raw.displayName : null,
        lastLogin: typeof raw.lastLogin === "string" ? raw.lastLogin : null,
        firebaseUser,
    };
};

const isBoolean = (value: unknown): value is boolean =>
    typeof value === "boolean";

const coerceDateLike = (value: unknown): string | Date | null => {
    if (value instanceof Date) {
        return value;
    }

    if (typeof value === "string" && value.trim().length > 0) {
        return value;
    }

    return null;
};

const coerceString = (value: unknown): string | null =>
    typeof value === "string" && value.trim().length > 0 ? value : null;

const isOrganisationUsageActivityStatus = (
    value: unknown,
): value is OrganisationUsageActivityStatus =>
    value === "active" ||
    value === "inactive" ||
    value === "cancelled" ||
    value === "unknown";

const toOrganisation = (candidate: unknown): Organisation | null => {
    if (!candidate || typeof candidate !== "object") {
        return null;
    }

    const raw = candidate as Record<string, unknown>;
    const id = typeof raw.id === "string" ? raw.id : null;
    const name = typeof raw.name === "string" ? raw.name : null;

    if (!id || !name) {
        return null;
    }

    const subscriptionStatus =
        typeof raw.subscriptionStatus === "number"
            ? raw.subscriptionStatus
            : OrgSubscriptionStatus.none;

    const users = Array.isArray(raw.users)
        ? raw.users
              .map(toOrganisationUser)
              .filter((user): user is OrganisationUser => user !== null)
        : [];

    const partnerUsers = Array.isArray(raw.partnerUsers)
        ? raw.partnerUsers
              .map(toOrganisationUser)
              .filter((user): user is OrganisationUser => user !== null)
        : undefined;

    const provider =
        raw.provider === "quickbooks" || raw.provider === "xero"
            ? raw.provider
            : null;

    return {
        id,
        tenantId: coerceString(raw.tenantId),
        name,
        subscriptionStatus,
        organisationType:
            typeof raw.organisationType === "number" ||
            typeof raw.organisationType === "string"
                ? raw.organisationType
                : null,
        provider,
        baseCurrency: coerceString(raw.baseCurrency),
        industry: coerceString(raw.industry),
        countryCode: coerceString(raw.countryCode),
        showAccural:
            isBoolean(raw.showAccural) || typeof raw.showAccural === "string"
                ? raw.showAccural
                : null,
        paysTax: isBoolean(raw.paysTax) ? raw.paysTax : null,
        operationsStartDate: coerceDateLike(raw.operationsStartDate),
        lastFullDataRefresh: coerceDateLike(raw.lastFullDataRefresh),
        createdAt: coerceDateLike(raw.createdAt),
        created_at: coerceDateLike(raw.created_at),
        itemsUpdated: coerceDateLike(raw.itemsUpdated),
        usageActivityStatus: isOrganisationUsageActivityStatus(
            raw.usageActivityStatus,
        )
            ? raw.usageActivityStatus
            : undefined,
        usageActiveMinutesLast7Days: coerceNumber(
            raw.usageActiveMinutesLast7Days,
            0,
        ),
        usageLastActiveDate:
            typeof raw.usageLastActiveDate === "string"
                ? raw.usageLastActiveDate
                : raw.usageLastActiveDate === null
                  ? null
                  : undefined,
        parentOrganisationId:
            typeof raw.parentOrganisationId === "string"
                ? raw.parentOrganisationId
                : null,
        users,
        ...(partnerUsers && partnerUsers.length > 0 && { partnerUsers }),
    };
};

// Helper function for providing list tags with individual entity tags
function providesList<
    R extends Array<{ id: string | number }>,
    T extends string,
>(resultsWithIds: R | undefined, tagType: T) {
    return resultsWithIds
        ? [
              { type: tagType, id: "LIST" },
              ...resultsWithIds.map(({ id }) => ({ type: tagType, id })),
          ]
        : [{ type: tagType, id: "LIST" }];
}

export type ApiEndpointBuilder = Parameters<
    Parameters<typeof createApi>[0]["endpoints"]
>[0];

export const apiSlice = createApi({
    reducerPath: "api",
    baseQuery: enhancedBaseQuery,
    // Comprehensive tag types for cache invalidation
    tagTypes: [
        "Organisation",
        "User",
        "EmailTemplate",
        "ContentBlock",
        "Log",
        "BusinessType",
        "SpoofSession",
        "ProviderToken",
        "SyncData",
        "StripeSubscription",
        "StripePrice",
        "StripeProduct",
        "StripeSubscriptionObjects",
        "AdminUserWithClaims",
        "UsageMetrics",
        "DemoAccess",
        "BulkEmailJob",
        "BugReport",
        "FeatureRequest",
        "AdminMetrics",
        "AdminSubscriptionLocalSnapshot",
        "AdminSubscriptionAuditRun",
        "AdminSubscriptionAction",
        "AdminSubscriptionStripeSummary",
        "DocsShareLink",
        "AdminV2Identity",
        "AdminV2Approvals",
        "AdminV2AuditLog",
        "AdminV2Jobs",
        "AdminV2RevenueAccess",
        "AdminV2RevenueTransactions",
        "AdminV2RevenueSubscriptions",
        "AdminV2RevenueDisputes",
        "AdminV2RevenueOffers",
        "AdminV2RevenueOnboardingFlows",
        "AdminV2RevenueCoupons",
        "AdminV2RevenueAffiliates",
        "AdminV2FeatureFlags",
        "AdminV2Entitlements",
        "AdminV2RbacStaffUsers",
        "AdminV2RbacRoles",
        "AdminV2RbacPermissions",
        "AdminV2CrmPeople",
        "AdminV2CrmBusinesses",
        "AdminV2CrmFirms",
        "AdminV2CrmFirmClientRelationships",
        "AdminV2LearningContent",
        "AdminV2LearningPathways",
        "AdminV2LearningAssignments",
        "AdminV2LearningProgress",
        "AdminV2LearningScores",
        "AdminV2LearningScoreRules",
        "AdminV2SalesDeals",
        "AdminV2SalesScores",
        "AdminV2SalesWorkQueues",
        "AdminV2SalesReporting",
        "AdminV2Integrations",
        "AdminV2EventTaxonomy",
        "AdminV2Ops",
        "AdminV2GrowthForms",
        "AdminV2GrowthTags",
        "AdminV2GrowthSegments",
        "AdminV2GrowthCampaigns",
        "AdminV2GrowthSequences",
        "AdminV2Automations",
        "AdminV2SupportTickets",
        "AdminV2MessagingSenderIdentities",
        "AdminV2MessagingTemplates",
        "AdminV2MessagingPreferences",
        "AdminV2MessagingInApp",
        "AdminV2HelpCollections",
        "AdminV2HelpArticles",
        "AdminV2HelpSearchLogs",
        "AdminV2HelpFeedback",
    ],
    // Enhanced caching configuration with better performance
    keepUnusedDataFor: 300, // 5 minutes (improved from default 60 seconds)
    refetchOnFocus: false, // Don't refetch when window gains focus (prevents excessive requests)
    refetchOnReconnect: true, // Refetch when network reconnects (keep this for data consistency)
    refetchOnMountOrArgChange: 300, // Only refetch if data is older than 5 minutes (matches keepUnusedDataFor)
    endpoints: builder => ({
        // Logs endpoint with proper caching - exactly matches legacy API structure
        getLogs: builder.query<Log[], number | void>({
            query: pageSize => ({
                url: "/admin/logs",
                params: { pageSize },
            }),
            providesTags: ["Log"],
            transformResponse: (res: RawLog[]) =>
                res.map(log => ({
                    ...log,
                    timestamp: new Date(log.timestamp),
                })),
            // Logs don't need to be cached as long
            keepUnusedDataFor: 120, // 2 minutes
        }),

        getSpoofSessions: builder.query<
            AdminSpoofSessionsResponse,
            { limit?: number } | void
        >({
            query: (params = {}) => ({
                url: "/admin/spoof-sessions",
                params,
            }),
            providesTags: [{ type: "SpoofSession", id: "LIST" }],
            keepUnusedDataFor: 60,
        }),

        getQuickbooksTrials: builder.query<AdminQuickbooksTrialsResponse, void>(
            {
                query: () => ({
                    url: "/admin/quickbooks-trials",
                }),
                keepUnusedDataFor: 60,
            },
        ),

        getBulkEmailJobs: builder.query<
            AdminBulkEmailJobsResponse,
            { limit?: number; status?: string } | void
        >({
            query: (params = {}) => ({
                url: "/admin/bulk-email-jobs",
                params,
            }),
            providesTags: [{ type: "BulkEmailJob", id: "LIST" }],
            keepUnusedDataFor: 30,
        }),

        // New Optimized Admin Endpoints (Backend Changes)

        // Fast organisation data with basic user info - optimized for admin dashboard
        getOrganisations: builder.query<
            AdminOrganisationSummary[],
            {
                search?: string;
                organisationType?: string;
                sortBy?: string;
            }
        >({
            query: params => ({
                url: "/admin/organisations",
                params,
            }),
            providesTags: result =>
                result
                    ? [
                          ...result.map(({ id }) => ({
                              type: "Organisation" as const,
                              id,
                          })),
                          { type: "Organisation", id: "LIST" },
                      ]
                    : [{ type: "Organisation", id: "LIST" }],
            transformResponse: (
                res: unknown[] = [],
            ): AdminOrganisationSummary[] =>
                res
                    .map(toOrganisation)
                    .filter(
                        (
                            organisation,
                        ): organisation is AdminOrganisationSummary =>
                            organisation !== null,
                    ),
            // Cache organisations longer since they change less frequently
            keepUnusedDataFor: 600, // 10 minutes
        }),

        removeOrganisationUser: builder.mutation<
            RemoveOrganisationUserResponse,
            RemoveOrganisationUserRequest
        >({
            query: ({ organisationId, userId }) => ({
                url: `/admin/organisations/${organisationId}/users/${userId}`,
                method: "DELETE",
            }),
            invalidatesTags: (_result, _error, { organisationId }) => [
                { type: "Organisation", id: organisationId },
                { type: "Organisation", id: "LIST" },
            ],
        }),

        // Detailed user data with organisation context - optimized for user management
        getUsers: builder.query<
            Array<{
                id: string;
                firebaseId: string;
                email: string;
                displayName?: string;
                activity_status:
                    | "active"
                    | "inactive"
                    | "never_logged_in"
                    | "cancelled"
                    | "unknown";
                lastLoginAt: string | null;
                loginCount: number;
                signupDate: string;
                usageActiveMinutesLast7Days: number;
                usageLastActiveDate: string | null;
                organisation_name: string;
                organisation_type: string;
                subscription_status: number;
                authProviders: AuthProvider[];
                providerConnections?: unknown[];
            }>,
            {
                search?: string;
                activityStatus?: string;
                sortBy?: string;
                sortOrder?: "asc" | "desc";
            }
        >({
            query: params => ({
                url: "/admin/users",
                params,
            }),
            providesTags: result =>
                result
                    ? [
                          ...result.map(({ id }) => ({
                              type: "User" as const,
                              id,
                          })),
                          { type: "User", id: "LIST" },
                      ]
                    : [{ type: "User", id: "LIST" }],
            transformResponse: (res: AllUser[]) => res.map(toAdminUserSummary),
            // Users data cached for shorter time as activity changes frequently
            keepUnusedDataFor: 180, // 3 minutes
        }),

        getProviderConnectionOrganisations: builder.query<
            AdminOrganisationSummary[],
            string
        >({
            query: connectionId => ({
                url: `/admin/provider-connection/${connectionId}/organisations`,
            }),
            providesTags: ["Organisation"],
            keepUnusedDataFor: 300,
        }),

        // Get dashboard statistics for admin
        getDashboardStatistics: builder.query<AdminStatisticsResponse, void>({
            query: () => ({
                url: "/admin/statistics",
            }),
            providesTags: ["SyncData"],
            transformResponse: (res: unknown): AdminStatisticsResponse => {
                const raw = res as Record<string, unknown>;
                const users =
                    raw.users && typeof raw.users === "object"
                        ? (raw.users as Record<string, unknown>)
                        : {};
                const organizations =
                    raw.organizations && typeof raw.organizations === "object"
                        ? (raw.organizations as Record<string, unknown>)
                        : {};
                const subscriptions =
                    raw.subscriptions && typeof raw.subscriptions === "object"
                        ? (raw.subscriptions as Record<string, unknown>)
                        : {};
                const stripe = raw.stripe as AdminStatisticsResponse["stripe"];

                return {
                    stripe,
                    users: {
                        total_users: coerceNumber(users.total_users),
                        active_users: coerceNumber(users.active_users),
                        never_logged_in: coerceNumber(users.never_logged_in),
                    },
                    organizations: {
                        total_orgs: coerceNumber(organizations.total_orgs),
                        advisor_accounts: coerceNumber(
                            organizations.advisor_accounts,
                        ),
                        business_accounts: coerceNumber(
                            organizations.business_accounts,
                        ),
                    },
                    subscriptions: {
                        monthly_subs: coerceNumber(subscriptions.monthly_subs),
                        lifetime_spots: coerceNumber(
                            subscriptions.lifetime_spots,
                        ),
                        cancelled: coerceNumber(subscriptions.cancelled),
                    },
                };
            },
            // Statistics change less frequently
            keepUnusedDataFor: 300, // 5 minutes
        }),

        getAdminMetrics: builder.query<AdminMetricsResponse, void>({
            query: () => ({
                url: "/admin/admin-metrics",
            }),
            providesTags: ["AdminMetrics"],
            keepUnusedDataFor: 60,
        }),

        getAdminV2Me: builder.query<AdminV2MeResponse, void>({
            query: () => ({
                url: "/admin/v2/me",
            }),
            providesTags: [{ type: "AdminV2Identity", id: "ME" }],
            keepUnusedDataFor: 60,
        }),

        ...buildAdminV2RevenueEndpoints(builder),

        getAdminV2LearningContents: builder.query<
            AdminV2LearningContentsResponse,
            {
                organisationId?: string;
                q?: string;
                status?: AdminV2LearningContentStatus;
                contentType?: AdminV2LearningContentType;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/learning/content",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2LearningContent", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        getAdminV2LearningContent: builder.query<
            AdminV2LearningContentDetail,
            { contentId: string }
        >({
            query: ({ contentId }) => ({
                url: `/admin/v2/learning/content/${contentId}`,
            }),
            providesTags: (_result, _error, { contentId }) => [
                { type: "AdminV2LearningContent", id: contentId },
            ],
            keepUnusedDataFor: 10,
        }),

        createAdminV2LearningContent: builder.mutation<
            AdminV2LearningContentDetail,
            {
                organisationId: string;
                title: string;
                contentKey?: string;
                summary?: string;
                contentType?: AdminV2LearningContentType;
                body?: AdminV2JsonValue;
                settings?: AdminV2JsonValue;
            }
        >({
            query: body => ({
                url: "/admin/v2/learning/content",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2LearningContent", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        updateAdminV2LearningContent: builder.mutation<
            AdminV2LearningContentDetail,
            {
                contentId: string;
                body: {
                    contentKey?: string;
                    title?: string;
                    summary?: string | null;
                    contentType?: AdminV2LearningContentType;
                    body?: AdminV2JsonValue;
                    settings?: AdminV2JsonValue;
                };
            }
        >({
            query: ({ contentId, body }) => ({
                url: `/admin/v2/learning/content/${contentId}`,
                method: "PUT",
                data: body,
            }),
            invalidatesTags: (_result, _error, { contentId }) => [
                { type: "AdminV2LearningContent", id: "LIST" },
                { type: "AdminV2LearningContent", id: contentId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        publishAdminV2LearningContent: builder.mutation<
            AdminV2LearningContentDetail,
            { contentId: string }
        >({
            query: ({ contentId }) => ({
                url: `/admin/v2/learning/content/${contentId}/publish`,
                method: "POST",
                data: {},
            }),
            invalidatesTags: (_result, _error, { contentId }) => [
                { type: "AdminV2LearningContent", id: "LIST" },
                { type: "AdminV2LearningContent", id: contentId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2LearningPathways: builder.query<
            AdminV2LearningPathwaysResponse,
            {
                organisationId?: string;
                q?: string;
                status?: AdminV2LearningPathwayStatus;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/learning/pathways",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2LearningPathways", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        getAdminV2LearningPathway: builder.query<
            AdminV2LearningPathwayDetail,
            { pathwayId: string }
        >({
            query: ({ pathwayId }) => ({
                url: `/admin/v2/learning/pathways/${pathwayId}`,
            }),
            providesTags: (_result, _error, { pathwayId }) => [
                { type: "AdminV2LearningPathways", id: pathwayId },
            ],
            keepUnusedDataFor: 10,
        }),

        createAdminV2LearningPathway: builder.mutation<
            AdminV2LearningPathwayDetail,
            {
                organisationId: string;
                name: string;
                pathwayKey?: string;
                description?: string;
                schema?: AdminV2JsonValue;
                settings?: AdminV2JsonValue;
            }
        >({
            query: body => ({
                url: "/admin/v2/learning/pathways",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2LearningPathways", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        updateAdminV2LearningPathway: builder.mutation<
            AdminV2LearningPathwayDetail,
            {
                pathwayId: string;
                body: {
                    pathwayKey?: string;
                    name?: string;
                    description?: string | null;
                    schema?: AdminV2JsonValue;
                    settings?: AdminV2JsonValue;
                };
            }
        >({
            query: ({ pathwayId, body }) => ({
                url: `/admin/v2/learning/pathways/${pathwayId}`,
                method: "PUT",
                data: body,
            }),
            invalidatesTags: (_result, _error, { pathwayId }) => [
                { type: "AdminV2LearningPathways", id: "LIST" },
                { type: "AdminV2LearningPathways", id: pathwayId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        publishAdminV2LearningPathway: builder.mutation<
            AdminV2LearningPathwayDetail,
            { pathwayId: string }
        >({
            query: ({ pathwayId }) => ({
                url: `/admin/v2/learning/pathways/${pathwayId}/publish`,
                method: "POST",
                data: {},
            }),
            invalidatesTags: (_result, _error, { pathwayId }) => [
                { type: "AdminV2LearningPathways", id: "LIST" },
                { type: "AdminV2LearningPathways", id: pathwayId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        assignAdminV2LearningPathway: builder.mutation<
            {
                assignment: AdminV2LearningAssignment;
                progress: AdminV2LearningProgress[];
            },
            {
                pathwayId: string;
                body: {
                    organisationId: string;
                    personId?: string;
                    businessId?: string;
                    dueAt?: string;
                    metadata?: AdminV2JsonValue;
                };
            }
        >({
            query: ({ pathwayId, body }) => ({
                url: `/admin/v2/learning/pathways/${pathwayId}/assign`,
                method: "POST",
                data: body,
            }),
            invalidatesTags: (_result, _error, { pathwayId }) => [
                { type: "AdminV2LearningPathways", id: "LIST" },
                { type: "AdminV2LearningPathways", id: pathwayId },
                { type: "AdminV2LearningAssignments", id: "LIST" },
                { type: "AdminV2LearningProgress", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2LearningAssignments: builder.query<
            AdminV2LearningAssignmentsResponse,
            {
                organisationId?: string;
                pathwayId?: string;
                personId?: string;
                businessId?: string;
                status?: AdminV2LearningAssignmentStatus;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/learning/assignments",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2LearningAssignments", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        getAdminV2LearningProgress: builder.query<
            AdminV2LearningProgressResponse,
            {
                organisationId?: string;
                assignmentId?: string;
                pathwayId?: string;
                contentId?: string;
                personId?: string;
                businessId?: string;
                status?: AdminV2LearningProgressStatus;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/learning/progress",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2LearningProgress", id: "LIST" }],
            keepUnusedDataFor: 10,
        }),

        updateAdminV2LearningProgress: builder.mutation<
            {
                progress: AdminV2LearningProgress;
                assignment: AdminV2LearningAssignment;
                score: AdminV2LearningRecomputeScoreResponse;
            },
            {
                organisationId: string;
                assignmentId: string;
                contentId: string;
                status: AdminV2LearningProgressStatus;
                progressPercent?: number;
                source?: string;
                metadata?: AdminV2JsonValue;
                completedAt?: string;
            }
        >({
            query: body => ({
                url: "/admin/v2/learning/progress",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2LearningProgress", id: "LIST" },
                { type: "AdminV2LearningAssignments", id: "LIST" },
                { type: "AdminV2LearningScores", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2LearningScoreRuleVersions: builder.query<
            AdminV2LearningScoreRuleVersionsResponse,
            {
                organisationId: string;
                scoreModelKey?: string;
                limit?: number;
                offset?: number;
            }
        >({
            query: params => ({
                url: "/admin/v2/learning/scores/rules",
                params,
            }),
            providesTags: [{ type: "AdminV2LearningScoreRules", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        createAdminV2LearningScoreRuleVersion: builder.mutation<
            {
                ruleVersion: AdminV2LearningScoreRuleVersionsResponse["ruleVersions"][number];
            },
            {
                organisationId: string;
                scoreModelKey?: string;
                rules: AdminV2LearningScoreRule[];
                notes?: string;
                publish?: boolean;
            }
        >({
            query: body => ({
                url: "/admin/v2/learning/scores/rules",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2LearningScoreRules", id: "LIST" },
                { type: "AdminV2LearningScores", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        publishAdminV2LearningScoreRuleVersion: builder.mutation<
            {
                ruleVersion: AdminV2LearningScoreRuleVersionsResponse["ruleVersions"][number];
            },
            { ruleVersionId: string; organisationId: string }
        >({
            query: ({ ruleVersionId, organisationId }) => ({
                url: `/admin/v2/learning/scores/rules/${ruleVersionId}/publish`,
                method: "POST",
                params: {
                    organisationId,
                },
                data: {},
            }),
            invalidatesTags: [
                { type: "AdminV2LearningScoreRules", id: "LIST" },
                { type: "AdminV2LearningScores", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2LearningPeopleScoreDashboard: builder.query<
            AdminV2LearningPeopleScoreDashboardResponse,
            {
                organisationId?: string;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/learning/scores/dashboard/people",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2LearningScores", id: "LIST" }],
            keepUnusedDataFor: 10,
        }),

        getAdminV2LearningBusinessScoreDashboard: builder.query<
            AdminV2LearningBusinessScoreDashboardResponse,
            {
                organisationId?: string;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/learning/scores/dashboard/businesses",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2LearningScores", id: "LIST" }],
            keepUnusedDataFor: 10,
        }),

        getAdminV2LearningScoreTimeline: builder.query<
            AdminV2LearningScoreTimelineResponse,
            {
                organisationId: string;
                personId?: string;
                businessId?: string;
                limit?: number;
                offset?: number;
            }
        >({
            query: params => ({
                url: "/admin/v2/learning/scores/timeline",
                params,
            }),
            providesTags: [{ type: "AdminV2LearningScores", id: "LIST" }],
            keepUnusedDataFor: 10,
        }),

        getAdminV2LearningScoreExplainability: builder.query<
            AdminV2LearningScoreExplainabilityResponse,
            {
                organisationId: string;
                scoreSnapshotId?: string;
                personId?: string;
                businessId?: string;
            }
        >({
            query: params => ({
                url: "/admin/v2/learning/scores/explainability",
                params,
            }),
            providesTags: [{ type: "AdminV2LearningScores", id: "LIST" }],
            keepUnusedDataFor: 10,
        }),

        recomputeAdminV2LearningScore: builder.mutation<
            AdminV2LearningRecomputeScoreResponse,
            {
                organisationId: string;
                personId?: string;
                businessId?: string;
            }
        >({
            query: body => ({
                url: "/admin/v2/learning/scores/recompute",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2LearningScores", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2SalesDeals: builder.query<
            AdminV2SalesDealsResponse,
            {
                organisationId?: string;
                ownerStaffUserId?: string;
                segment?: string;
                stage?: AdminV2SalesStage;
                scoreBand?: AdminV2SalesScoreBand;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/sales/deals",
                params: params ?? undefined,
            }),
            providesTags: result =>
                providesList(result?.deals ?? [], "AdminV2SalesDeals"),
            keepUnusedDataFor: 20,
        }),

        createAdminV2SalesDeal: builder.mutation<
            AdminV2SalesDealMutationResponse,
            {
                organisationId: string;
                personId?: string;
                businessId?: string;
                title: string;
                stage?: AdminV2SalesStage;
                ownerStaffUserId?: string;
                segment?: string;
                amountCents?: number;
                currency?: string;
                closeProbabilityPercent?: number;
                expectedCloseDate?: string;
                nextActionAt?: string;
                metadata?: AdminV2JsonValue;
            }
        >({
            query: body => ({
                url: "/admin/v2/sales/deals",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2SalesDeals", id: "LIST" },
                { type: "AdminV2SalesScores", id: "LIST" },
                { type: "AdminV2SalesWorkQueues", id: "LIST" },
                { type: "AdminV2SalesReporting", id: "SUMMARY" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        updateAdminV2SalesDealStage: builder.mutation<
            AdminV2SalesDealMutationResponse,
            {
                dealId: string;
                body: {
                    stage: AdminV2SalesStage;
                    note?: string;
                };
            }
        >({
            query: ({ dealId, body }) => ({
                url: `/admin/v2/sales/deals/${dealId}/stage`,
                method: "POST",
                data: body,
            }),
            invalidatesTags: (_result, _error, { dealId }) => [
                { type: "AdminV2SalesDeals", id: "LIST" },
                { type: "AdminV2SalesDeals", id: dealId },
                { type: "AdminV2SalesScores", id: "LIST" },
                { type: "AdminV2SalesWorkQueues", id: "LIST" },
                { type: "AdminV2SalesReporting", id: "SUMMARY" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        claimAdminV2SalesDeal: builder.mutation<
            AdminV2SalesDealEventMutationResponse,
            {
                dealId: string;
                body?: {
                    assigneeStaffUserId?: string;
                    expectedOwnerStaffUserId?: string | null;
                    note?: string;
                };
            }
        >({
            query: ({ dealId, body }) => ({
                url: `/admin/v2/sales/deals/${dealId}/claim`,
                method: "POST",
                data: body ?? {},
            }),
            invalidatesTags: (_result, _error, { dealId }) => [
                { type: "AdminV2SalesDeals", id: "LIST" },
                { type: "AdminV2SalesDeals", id: dealId },
                { type: "AdminV2SalesWorkQueues", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        addAdminV2SalesDealNote: builder.mutation<
            AdminV2SalesDealEventMutationResponse,
            {
                dealId: string;
                body: {
                    note: string;
                };
            }
        >({
            query: ({ dealId, body }) => ({
                url: `/admin/v2/sales/deals/${dealId}/notes`,
                method: "POST",
                data: body,
            }),
            invalidatesTags: (_result, _error, { dealId }) => [
                { type: "AdminV2SalesDeals", id: "LIST" },
                { type: "AdminV2SalesDeals", id: dealId },
                { type: "AdminV2SalesWorkQueues", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2SalesDealEvents: builder.query<
            AdminV2SalesDealEventsResponse,
            {
                dealId: string;
                limit?: number;
                offset?: number;
            }
        >({
            query: ({ dealId, ...params }) => ({
                url: `/admin/v2/sales/deals/${dealId}/events`,
                params,
            }),
            providesTags: (_result, _error, { dealId }) => [
                { type: "AdminV2SalesDeals", id: dealId },
            ],
            keepUnusedDataFor: 20,
        }),

        getAdminV2SalesLeadScores: builder.query<
            AdminV2SalesLeadScoresResponse,
            {
                organisationId?: string;
                personId?: string;
                ownerStaffUserId?: string;
                segment?: string;
                scoreBand?: AdminV2SalesScoreBand;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/sales/scores/leads",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2SalesScores", id: "LIST" }],
            keepUnusedDataFor: 10,
        }),

        getAdminV2SalesMemberScores: builder.query<
            AdminV2SalesMemberScoresResponse,
            {
                organisationId?: string;
                businessId?: string;
                ownerStaffUserId?: string;
                segment?: string;
                scoreBand?: AdminV2SalesScoreBand;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/sales/scores/members",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2SalesScores", id: "LIST" }],
            keepUnusedDataFor: 10,
        }),

        recomputeAdminV2SalesScores: builder.mutation<
            AdminV2SalesRecomputeScoresResponse,
            {
                organisationId: string;
                personId?: string;
                businessId?: string;
            }
        >({
            query: body => ({
                url: "/admin/v2/sales/scores/recompute",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2SalesScores", id: "LIST" },
                { type: "AdminV2SalesWorkQueues", id: "LIST" },
                { type: "AdminV2SalesReporting", id: "SUMMARY" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2SalesWorkQueues: builder.query<
            AdminV2SalesWorkQueueResponse,
            {
                role?: AdminV2SalesQueueRole;
                organisationId?: string;
                ownerStaffUserId?: string;
                segment?: string;
                scoreBand?: AdminV2SalesScoreBand;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/sales/work-queues",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2SalesWorkQueues", id: "LIST" }],
            keepUnusedDataFor: 10,
        }),

        getAdminV2SalesReportingSummary: builder.query<
            AdminV2SalesReportingSummaryResponse,
            {
                organisationId: string;
            }
        >({
            query: params => ({
                url: "/admin/v2/sales/reporting/summary",
                params,
            }),
            providesTags: [{ type: "AdminV2SalesReporting", id: "SUMMARY" }],
            keepUnusedDataFor: 10,
        }),

        getAdminV2Integrations: builder.query<
            AdminV2IntegrationsResponse,
            { organisationId?: string } | void
        >({
            query: params => ({
                url: "/admin/v2/integrations",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2Integrations", id: "LIST" }],
            keepUnusedDataFor: 15,
        }),

        upsertAdminV2IntegrationConnection: builder.mutation<
            AdminV2IntegrationConnectionResponse,
            {
                organisationId?: string | null;
                integrationKey: string;
                status?: AdminV2IntegrationConnectionStatus;
                connectionLabel?: string | null;
                connectionConfig?: AdminV2JsonValue;
            }
        >({
            query: body => ({
                url: "/admin/v2/integrations/connections",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2Integrations", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        patchAdminV2IntegrationConnection: builder.mutation<
            AdminV2IntegrationConnectionResponse,
            {
                connectionId: string;
                body: {
                    status?: AdminV2IntegrationConnectionStatus;
                    connectionLabel?: string | null;
                    connectionConfig?: AdminV2JsonValue;
                };
            }
        >({
            query: ({ connectionId, body }) => ({
                url: `/admin/v2/integrations/connections/${connectionId}`,
                method: "PATCH",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2Integrations", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2IntegrationConnectionHealth: builder.query<
            AdminV2IntegrationConnectionHealthResponse,
            {
                connectionId: string;
            }
        >({
            query: ({ connectionId }) => ({
                url: `/admin/v2/integrations/connections/${connectionId}/health`,
            }),
            providesTags: [{ type: "AdminV2Integrations", id: "LIST" }],
            keepUnusedDataFor: 15,
        }),

        getAdminV2PosthogDashboards: builder.query<
            AdminV2PosthogDashboardsResponse,
            void
        >({
            query: () => ({
                url: "/admin/v2/insights/posthog/dashboards",
            }),
            keepUnusedDataFor: 60,
        }),

        getAdminV2EventTaxonomy: builder.query<
            AdminV2EventTaxonomyResponse,
            void
        >({
            query: () => ({
                url: "/admin/v2/insights/event-taxonomy",
            }),
            providesTags: [{ type: "AdminV2EventTaxonomy", id: "LIST" }],
            keepUnusedDataFor: 60,
        }),

        patchAdminV2EventTaxonomy: builder.mutation<
            AdminV2PatchEventTaxonomyResponse,
            {
                eventName: string;
                body: {
                    status?: AdminV2ProductEventTaxonomyStatus;
                    annotation?: string | null;
                    deprecatedReason?: string | null;
                    replacementEventName?: string | null;
                };
            }
        >({
            query: ({ eventName, body }) => ({
                url: `/admin/v2/insights/event-taxonomy/${encodeURIComponent(
                    eventName,
                )}`,
                method: "PATCH",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2EventTaxonomy", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2OpsWhatNeedsAttention: builder.query<
            AdminV2OpsWhatNeedsAttentionResponse,
            void
        >({
            query: () => ({
                url: "/admin/v2/ops/what-needs-attention",
            }),
            providesTags: [{ type: "AdminV2Ops", id: "ATTENTION" }],
            keepUnusedDataFor: 15,
        }),

        getAdminV2OpsIngestFailures: builder.query<
            AdminV2OpsIngestFailuresResponse,
            void
        >({
            query: () => ({
                url: "/admin/v2/ops/ingest-failures",
            }),
            providesTags: [{ type: "AdminV2Ops", id: "INGEST" }],
            keepUnusedDataFor: 15,
        }),

        getAdminV2OpsDeliverabilityHealth: builder.query<
            AdminV2OpsDeliverabilityHealthResponse,
            void
        >({
            query: () => ({
                url: "/admin/v2/ops/deliverability-health",
            }),
            providesTags: [{ type: "AdminV2Ops", id: "DELIVERABILITY" }],
            keepUnusedDataFor: 15,
        }),

        getAdminV2OpsSupportBacklog: builder.query<
            AdminV2OpsSupportBacklogResponse,
            void
        >({
            query: () => ({
                url: "/admin/v2/ops/support-backlog",
            }),
            providesTags: [{ type: "AdminV2Ops", id: "SUPPORT_BACKLOG" }],
            keepUnusedDataFor: 15,
        }),

        getAdminV2FeatureFlags: builder.query<
            AdminV2FeatureFlagsResponse,
            {
                organisationId?: string;
                includeGlobal?: boolean;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/settings/feature-flags",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2FeatureFlags", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        upsertAdminV2FeatureFlag: builder.mutation<
            AdminV2FeatureFlagUpsertResponse,
            {
                organisationId?: string;
                flagKey: string;
                isEnabled: boolean;
                rolloutPercentage?: number;
                description?: string;
                metadata?: AdminV2JsonValue;
            }
        >({
            query: body => ({
                url: "/admin/v2/settings/feature-flags",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2FeatureFlags", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
                { type: "AdminV2RevenueAccess", id: "CONTEXT" },
            ],
        }),

        getAdminV2Entitlements: builder.query<
            AdminV2EntitlementsResponse,
            {
                organisationId?: string;
                includeGlobal?: boolean;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/settings/entitlements",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2Entitlements", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        upsertAdminV2Entitlement: builder.mutation<
            AdminV2EntitlementUpsertResponse,
            {
                organisationId?: string;
                entitlementKey: string;
                isEnabled: boolean;
                source?: "admin" | "system" | "stripe";
                value?: AdminV2JsonValue | null;
                startsAt?: string;
                expiresAt?: string;
            }
        >({
            query: body => ({
                url: "/admin/v2/settings/entitlements",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2Entitlements", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
                { type: "AdminV2RevenueAccess", id: "CONTEXT" },
            ],
        }),

        getAdminV2RbacStaffUsers: builder.query<
            AdminV2RbacStaffUsersResponse,
            { search?: string; limit?: number } | void
        >({
            query: params => ({
                url: "/admin/v2/rbac/staff-users",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2RbacStaffUsers", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        getAdminV2RbacRoles: builder.query<AdminV2RbacRolesResponse, void>({
            query: () => ({
                url: "/admin/v2/rbac/roles",
            }),
            providesTags: [{ type: "AdminV2RbacRoles", id: "LIST" }],
            keepUnusedDataFor: 60,
        }),

        getAdminV2RbacPermissions: builder.query<
            AdminV2RbacPermissionsResponse,
            void
        >({
            query: () => ({
                url: "/admin/v2/rbac/permissions",
            }),
            providesTags: [{ type: "AdminV2RbacPermissions", id: "LIST" }],
            keepUnusedDataFor: 60,
        }),

        assignAdminV2StaffRole: builder.mutation<
            { staffUser: AdminV2RbacStaffUsersResponse["staffUsers"][number] },
            { staffUserId: string; roleKey: string; assign: boolean }
        >({
            query: ({ staffUserId, roleKey, assign }) => ({
                url: `/admin/v2/rbac/staff-users/${staffUserId}/roles`,
                method: "POST",
                data: {
                    roleKey,
                    assign,
                },
            }),
            invalidatesTags: [
                { type: "AdminV2RbacStaffUsers", id: "LIST" },
                { type: "AdminV2Identity", id: "ME" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        setAdminV2StaffPermissionOverride: builder.mutation<
            { staffUser: AdminV2RbacStaffUsersResponse["staffUsers"][number] },
            {
                staffUserId: string;
                permissionKey: string;
                isGranted: boolean;
                reason?: string;
            }
        >({
            query: ({ staffUserId, permissionKey, isGranted, reason }) => ({
                url: `/admin/v2/rbac/staff-users/${staffUserId}/permission-overrides`,
                method: "POST",
                data: {
                    permissionKey,
                    isGranted,
                    ...(reason ? { reason } : {}),
                },
            }),
            invalidatesTags: [
                { type: "AdminV2RbacStaffUsers", id: "LIST" },
                { type: "AdminV2Identity", id: "ME" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        searchAdminV2: builder.query<
            AdminV2SearchResponse,
            { q: string; types?: string[]; limit?: number }
        >({
            query: ({ q, types, limit }) => ({
                url: "/admin/v2/search",
                params: {
                    q,
                    ...(types && types.length > 0
                        ? { types: types.join(",") }
                        : {}),
                    ...(limit ? { limit } : {}),
                },
            }),
            keepUnusedDataFor: 10,
        }),

        getAdminV2Approvals: builder.query<
            AdminV2ApprovalsResponse,
            {
                status?: AdminV2ApprovalStatus;
                requestType?: string;
                limit?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/approvals",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2Approvals", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        createAdminV2Approval: builder.mutation<
            { approval: AdminV2ApprovalsResponse["approvals"][number] },
            {
                requestType: string;
                reason: string;
                targetType?: string;
                targetId?: string;
                requiredPermissionKey?: string;
                payload?: AdminV2JsonValue;
                expiresAt?: string;
            }
        >({
            query: body => ({
                url: "/admin/v2/approvals",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2Approvals", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        approveAdminV2Approval: builder.mutation<
            { approval: AdminV2ApprovalsResponse["approvals"][number] },
            { approvalRequestId: string; comment?: string }
        >({
            query: ({ approvalRequestId, comment }) => ({
                url: `/admin/v2/approvals/${approvalRequestId}/approve`,
                method: "POST",
                data: comment ? { comment } : {},
            }),
            invalidatesTags: [
                { type: "AdminV2Approvals", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        rejectAdminV2Approval: builder.mutation<
            { approval: AdminV2ApprovalsResponse["approvals"][number] },
            { approvalRequestId: string; comment?: string }
        >({
            query: ({ approvalRequestId, comment }) => ({
                url: `/admin/v2/approvals/${approvalRequestId}/reject`,
                method: "POST",
                data: comment ? { comment } : {},
            }),
            invalidatesTags: [
                { type: "AdminV2Approvals", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        cancelAdminV2Approval: builder.mutation<
            { approval: AdminV2ApprovalsResponse["approvals"][number] },
            { approvalRequestId: string; comment?: string }
        >({
            query: ({ approvalRequestId, comment }) => ({
                url: `/admin/v2/approvals/${approvalRequestId}/cancel`,
                method: "POST",
                data: comment ? { comment } : {},
            }),
            invalidatesTags: [
                { type: "AdminV2Approvals", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2AuditLog: builder.query<
            AdminV2AuditLogResponse,
            {
                actor?: string;
                target?: string;
                action?: string;
                fromDate?: string;
                toDate?: string;
                limit?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/audit-log",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2AuditLog", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        getAdminV2Jobs: builder.query<
            AdminV2JobsResponse,
            {
                status?:
                    | "queued"
                    | "running"
                    | "succeeded"
                    | "failed"
                    | "cancelled";
                jobType?: string;
                organisationId?: string;
                limit?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/jobs",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2Jobs", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        getAdminV2Job: builder.query<AdminV2JobResponse, { jobId: string }>({
            query: ({ jobId }) => ({
                url: `/admin/v2/jobs/${jobId}`,
            }),
            providesTags: (_result, _error, { jobId }) => [
                { type: "AdminV2Jobs", id: jobId },
            ],
            keepUnusedDataFor: 10,
        }),

        cancelAdminV2Job: builder.mutation<
            AdminV2JobCancelResponse,
            { jobId: string }
        >({
            query: ({ jobId }) => ({
                url: `/admin/v2/jobs/${jobId}/cancel`,
                method: "POST",
                data: {},
            }),
            invalidatesTags: (_result, _error, { jobId }) => [
                { type: "AdminV2Jobs", id: "LIST" },
                { type: "AdminV2Jobs", id: jobId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2CrmPeople: builder.query<
            AdminV2CrmPeopleListResponse,
            {
                q?: string;
                lifecycleStage?: string;
                organisationId?: string;
                limit?: number;
                offset?: number;
                sortBy?:
                    | "full_name"
                    | "last_activity_at"
                    | "updated_at"
                    | "created_at";
                sortDirection?: "asc" | "desc";
            } | void
        >({
            query: params => ({
                url: "/admin/v2/crm/people",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2CrmPeople", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        bulkUpdateAdminV2CrmPeopleLifecycleStage: builder.mutation<
            AdminV2CrmPeopleBulkLifecycleStageResponse,
            {
                organisationId: string;
                lifecycleStage: string;
                personIds: string[];
            }
        >({
            query: body => ({
                url: "/admin/v2/crm/people/bulk-actions/lifecycle-stage",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2Jobs", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2CrmPersonRecord: builder.query<
            AdminV2CrmPersonRecordResponse,
            { personId: string }
        >({
            query: ({ personId }) => ({
                url: `/admin/v2/crm/people/${personId}`,
            }),
            providesTags: (_result, _error, { personId }) => [
                { type: "AdminV2CrmPeople", id: personId },
            ],
            keepUnusedDataFor: 20,
        }),

        updateAdminV2CrmPerson: builder.mutation<
            { person: AdminV2CrmPersonRecordResponse["person"] },
            {
                personId: string;
                body: {
                    organisationId?: string;
                    primaryEmail?: string;
                    phoneE164?: string | null;
                    fullName?: string;
                    lifecycleStage?: string;
                    consentPreferences?: JsonValue;
                    tags?: string[];
                    notes?: string | null;
                    lastActivityAt?: string | null;
                };
            }
        >({
            query: ({ personId, body }) => ({
                url: `/admin/v2/crm/people/${personId}`,
                method: "PUT",
                data: body,
            }),
            invalidatesTags: (_result, _error, { personId }) => [
                { type: "AdminV2CrmPeople", id: "LIST" },
                { type: "AdminV2CrmPeople", id: personId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2CrmBusinesses: builder.query<
            AdminV2CrmBusinessesListResponse,
            {
                q?: string;
                limit?: number;
                offset?: number;
                sortBy?: "name" | "updated_at" | "created_at";
                sortDirection?: "asc" | "desc";
            } | void
        >({
            query: params => ({
                url: "/admin/v2/crm/businesses",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2CrmBusinesses", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        getAdminV2CrmBusinessRecord: builder.query<
            AdminV2CrmBusinessRecordResponse,
            { businessId: string }
        >({
            query: ({ businessId }) => ({
                url: `/admin/v2/crm/businesses/${businessId}`,
            }),
            providesTags: (_result, _error, { businessId }) => [
                { type: "AdminV2CrmBusinesses", id: businessId },
            ],
            keepUnusedDataFor: 20,
        }),

        getAdminV2CrmFirms: builder.query<
            AdminV2CrmFirmsListResponse,
            {
                q?: string;
                limit?: number;
                offset?: number;
                sortBy?: "name" | "updated_at" | "created_at";
                sortDirection?: "asc" | "desc";
            } | void
        >({
            query: params => ({
                url: "/admin/v2/crm/firms",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2CrmFirms", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        getAdminV2CrmFirmRecord: builder.query<
            AdminV2CrmFirmRecordResponse,
            { firmId: string }
        >({
            query: ({ firmId }) => ({
                url: `/admin/v2/crm/firms/${firmId}`,
            }),
            providesTags: (_result, _error, { firmId }) => [
                { type: "AdminV2CrmFirms", id: firmId },
            ],
            keepUnusedDataFor: 20,
        }),

        getAdminV2CrmFirmClientRelationships: builder.query<
            AdminV2CrmFirmClientRelationshipsListResponse,
            {
                q?: string;
                firmId?: string;
                clientOrganisationId?: string;
                status?: AdminV2CrmRelationshipStatus;
                limit?: number;
                offset?: number;
                sortBy?: "updated_at" | "created_at" | "status";
                sortDirection?: "asc" | "desc";
            } | void
        >({
            query: params => ({
                url: "/admin/v2/crm/firm-client-relationships",
                params: params ?? undefined,
            }),
            providesTags: [
                { type: "AdminV2CrmFirmClientRelationships", id: "LIST" },
            ],
            keepUnusedDataFor: 20,
        }),

        getAdminV2CrmFirmClientRelationshipRecord: builder.query<
            AdminV2CrmFirmClientRelationshipRecordResponse,
            { relationshipId: string }
        >({
            query: ({ relationshipId }) => ({
                url: `/admin/v2/crm/firm-client-relationships/${relationshipId}`,
            }),
            providesTags: (_result, _error, { relationshipId }) => [
                {
                    type: "AdminV2CrmFirmClientRelationships",
                    id: relationshipId,
                },
            ],
            keepUnusedDataFor: 20,
        }),

        updateAdminV2CrmFirmClientRelationship: builder.mutation<
            {
                relationship: AdminV2CrmFirmClientRelationshipRecordResponse["relationship"];
            },
            {
                relationshipId: string;
                body: {
                    status?: AdminV2CrmRelationshipStatus;
                    billingResponsibility?: AdminV2CrmBillingResponsibility;
                    accountManagerUserId?: string | null;
                    notes?: string | null;
                    metadata?: JsonValue;
                };
            }
        >({
            query: ({ relationshipId, body }) => ({
                url: `/admin/v2/crm/firm-client-relationships/${relationshipId}`,
                method: "PUT",
                data: body,
            }),
            invalidatesTags: (_result, _error, { relationshipId }) => [
                { type: "AdminV2CrmFirmClientRelationships", id: "LIST" },
                {
                    type: "AdminV2CrmFirmClientRelationships",
                    id: relationshipId,
                },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2CrmTimeline: builder.query<
            AdminV2CrmTimelineResponse,
            {
                aggregateType?: string;
                aggregateId?: string;
                categories?: AdminV2CrmTimelineCategory[];
                organisationId: string;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/crm/timeline",
                params: params
                    ? {
                          ...params,
                          ...(params.categories && params.categories.length > 0
                              ? { categories: params.categories.join(",") }
                              : {}),
                      }
                    : undefined,
            }),
            keepUnusedDataFor: 10,
        }),

        createAdminV2CrmImportRun: builder.mutation<
            AdminV2CrmImportRunResponse,
            {
                importType: string;
                dryRun?: boolean;
                sourceFilename?: string;
                source?: string;
                organisationId: string;
                idempotencyKey?: string;
                columnMapping?: {
                    primaryEmail?: string;
                    fullName?: string;
                    lifecycleStage?: string;
                    phoneE164?: string;
                    notes?: string;
                    organisationId?: string;
                };
                rawCsv: string;
            }
        >({
            query: body => ({
                url: "/admin/v2/crm/import-runs",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2CrmPeople", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2CrmImportRun: builder.query<
            AdminV2CrmImportRunResponse,
            string
        >({
            query: importRunId => ({
                url: `/admin/v2/crm/import-runs/${importRunId}`,
            }),
            keepUnusedDataFor: 10,
        }),

        getAdminV2MessagingSenderIdentities: builder.query<
            AdminV2SenderIdentitiesResponse,
            {
                organisationId?: string;
                includeArchived?: boolean;
                q?: string;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/messaging/sender-identities",
                params: params ?? undefined,
            }),
            providesTags: [
                { type: "AdminV2MessagingSenderIdentities", id: "LIST" },
            ],
            keepUnusedDataFor: 20,
        }),

        createAdminV2MessagingSenderIdentity: builder.mutation<
            AdminV2SenderIdentitiesResponse["identities"][number],
            {
                organisationId?: string;
                displayName: string;
                email: string;
                category?: AdminV2SenderIdentityCategory;
                verificationStatus?: AdminV2SenderIdentityVerificationStatus;
                verificationError?: string | null;
                postmarkMessageStream: string;
                replyRouting?: AdminV2SenderReplyRouting;
                metadata?: AdminV2JsonValue;
            }
        >({
            query: body => ({
                url: "/admin/v2/messaging/sender-identities",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2MessagingSenderIdentities", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        updateAdminV2MessagingSenderIdentity: builder.mutation<
            AdminV2SenderIdentitiesResponse["identities"][number],
            {
                senderIdentityId: string;
                body: {
                    displayName?: string;
                    email?: string;
                    category?: AdminV2SenderIdentityCategory;
                    verificationStatus?: AdminV2SenderIdentityVerificationStatus;
                    verificationError?: string | null;
                    postmarkMessageStream?: string;
                    replyRouting?: AdminV2SenderReplyRouting;
                    metadata?: AdminV2JsonValue;
                };
            }
        >({
            query: ({ senderIdentityId, body }) => ({
                url: `/admin/v2/messaging/sender-identities/${senderIdentityId}`,
                method: "PUT",
                data: body,
            }),
            invalidatesTags: (_result, _error, { senderIdentityId }) => [
                { type: "AdminV2MessagingSenderIdentities", id: "LIST" },
                {
                    type: "AdminV2MessagingSenderIdentities",
                    id: senderIdentityId,
                },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        archiveAdminV2MessagingSenderIdentity: builder.mutation<
            AdminV2SenderIdentitiesResponse["identities"][number],
            { senderIdentityId: string }
        >({
            query: ({ senderIdentityId }) => ({
                url: `/admin/v2/messaging/sender-identities/${senderIdentityId}/archive`,
                method: "POST",
                data: {},
            }),
            invalidatesTags: (_result, _error, { senderIdentityId }) => [
                { type: "AdminV2MessagingSenderIdentities", id: "LIST" },
                {
                    type: "AdminV2MessagingSenderIdentities",
                    id: senderIdentityId,
                },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2MessagingTemplates: builder.query<
            AdminV2MessageTemplatesResponse,
            {
                organisationId?: string;
                includeArchived?: boolean;
                q?: string;
                status?: AdminV2MessageTemplateStatus;
                channel?: AdminV2MessagingChannel;
                category?: AdminV2MessagingCategory;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/messaging/templates",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2MessagingTemplates", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        getAdminV2MessagingTemplate: builder.query<
            AdminV2MessageTemplatesResponse["templates"][number],
            { templateId: string }
        >({
            query: ({ templateId }) => ({
                url: `/admin/v2/messaging/templates/${templateId}`,
            }),
            providesTags: (_result, _error, { templateId }) => [
                { type: "AdminV2MessagingTemplates", id: templateId },
            ],
            keepUnusedDataFor: 20,
        }),

        createAdminV2MessagingTemplate: builder.mutation<
            AdminV2MessageTemplatesResponse["templates"][number],
            {
                organisationId?: string;
                templateKey: string;
                name: string;
                channel?: AdminV2MessagingChannel;
                category?: AdminV2MessagingCategory;
                description?: string | null;
                metadata?: AdminV2JsonValue;
                draft: {
                    subject?: string | null;
                    bodyMarkdown: string;
                    bodyRenderedHtml?: string | null;
                    bodyText?: string | null;
                    allowedVariables?: string[];
                    metadata?: AdminV2JsonValue;
                };
            }
        >({
            query: body => ({
                url: "/admin/v2/messaging/templates",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2MessagingTemplates", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        updateAdminV2MessagingTemplate: builder.mutation<
            AdminV2MessageTemplatesResponse["templates"][number],
            {
                templateId: string;
                body: {
                    name?: string;
                    channel?: AdminV2MessagingChannel;
                    category?: AdminV2MessagingCategory;
                    description?: string | null;
                    metadata?: AdminV2JsonValue;
                    draft: {
                        subject?: string | null;
                        bodyMarkdown: string;
                        bodyRenderedHtml?: string | null;
                        bodyText?: string | null;
                        allowedVariables?: string[];
                        metadata?: AdminV2JsonValue;
                    };
                };
            }
        >({
            query: ({ templateId, body }) => ({
                url: `/admin/v2/messaging/templates/${templateId}`,
                method: "PUT",
                data: body,
            }),
            invalidatesTags: (_result, _error, { templateId }) => [
                { type: "AdminV2MessagingTemplates", id: "LIST" },
                { type: "AdminV2MessagingTemplates", id: templateId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        publishAdminV2MessagingTemplate: builder.mutation<
            AdminV2MessageTemplatesResponse["templates"][number],
            { templateId: string }
        >({
            query: ({ templateId }) => ({
                url: `/admin/v2/messaging/templates/${templateId}/publish`,
                method: "POST",
                data: {},
            }),
            invalidatesTags: (_result, _error, { templateId }) => [
                { type: "AdminV2MessagingTemplates", id: "LIST" },
                { type: "AdminV2MessagingTemplates", id: templateId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        archiveAdminV2MessagingTemplate: builder.mutation<
            AdminV2MessageTemplatesResponse["templates"][number],
            { templateId: string }
        >({
            query: ({ templateId }) => ({
                url: `/admin/v2/messaging/templates/${templateId}/archive`,
                method: "POST",
                data: {},
            }),
            invalidatesTags: (_result, _error, { templateId }) => [
                { type: "AdminV2MessagingTemplates", id: "LIST" },
                { type: "AdminV2MessagingTemplates", id: templateId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        previewAdminV2MessagingTemplate: builder.mutation<
            AdminV2MessageTemplatePreviewResponse,
            {
                templateId: string;
                body: {
                    version?: "draft" | "published";
                    samplePerson?: AdminV2JsonValue;
                    sampleBusiness?: AdminV2JsonValue;
                    variables?: AdminV2JsonValue;
                };
            }
        >({
            query: ({ templateId, body }) => ({
                url: `/admin/v2/messaging/templates/${templateId}/preview`,
                method: "POST",
                data: body,
            }),
        }),

        getAdminV2MessagingPersonPreferences: builder.query<
            AdminV2PersonCommunicationPreferencesResponse,
            { personId: string }
        >({
            query: ({ personId }) => ({
                url: `/admin/v2/messaging/preferences/people/${personId}`,
            }),
            providesTags: (_result, _error, { personId }) => [
                { type: "AdminV2MessagingPreferences", id: personId },
            ],
            keepUnusedDataFor: 10,
        }),

        getAdminV2MessagingPersonInAppMessages: builder.query<
            AdminV2InAppMessagesResponse,
            {
                personId: string;
                unreadOnly?: boolean;
                limit?: number;
                offset?: number;
            }
        >({
            query: ({ personId, ...params }) => ({
                url: `/admin/v2/messaging/in-app/people/${personId}`,
                params,
            }),
            providesTags: (_result, _error, { personId }) => [
                { type: "AdminV2MessagingInApp", id: personId },
            ],
            keepUnusedDataFor: 10,
        }),

        markAdminV2MessagingInAppMessageRead: builder.mutation<
            AdminV2InAppMessagesResponse["items"][number]["recipient"],
            {
                personId: string;
                messageRecipientId: string;
            }
        >({
            query: ({ messageRecipientId }) => ({
                url: `/admin/v2/messaging/in-app/${messageRecipientId}/read`,
                method: "POST",
                data: {},
            }),
            invalidatesTags: (
                _result,
                _error,
                { personId, messageRecipientId },
            ) => [
                { type: "AdminV2MessagingInApp", id: personId },
                { type: "AdminV2MessagingInApp", id: messageRecipientId },
            ],
        }),

        getAdminV2GrowthForms: builder.query<
            AdminV2GrowthFormsListResponse,
            {
                organisationId?: string;
                q?: string;
                status?: AdminV2GrowthFormStatus;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/growth/forms",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2GrowthForms", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        createAdminV2GrowthForm: builder.mutation<
            AdminV2GrowthFormDetailResponse,
            {
                organisationId: string;
                name: string;
                slug?: string;
                description?: string | null;
                schema?: AdminV2JsonValue;
                settings?: AdminV2JsonValue;
            }
        >({
            query: body => ({
                url: "/admin/v2/growth/forms",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2GrowthForms", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2GrowthForm: builder.query<
            AdminV2GrowthFormDetailResponse,
            { formId: string }
        >({
            query: ({ formId }) => ({
                url: `/admin/v2/growth/forms/${formId}`,
            }),
            providesTags: (_result, _error, { formId }) => [
                { type: "AdminV2GrowthForms", id: formId },
            ],
            keepUnusedDataFor: 20,
        }),

        updateAdminV2GrowthForm: builder.mutation<
            AdminV2GrowthFormDetailResponse,
            {
                formId: string;
                body: {
                    name?: string;
                    slug?: string;
                    description?: string | null;
                    schema?: AdminV2JsonValue;
                    settings?: AdminV2JsonValue;
                };
            }
        >({
            query: ({ formId, body }) => ({
                url: `/admin/v2/growth/forms/${formId}`,
                method: "PUT",
                data: body,
            }),
            invalidatesTags: (_result, _error, { formId }) => [
                { type: "AdminV2GrowthForms", id: "LIST" },
                { type: "AdminV2GrowthForms", id: formId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        publishAdminV2GrowthForm: builder.mutation<
            AdminV2GrowthFormDetailResponse,
            { formId: string }
        >({
            query: ({ formId }) => ({
                url: `/admin/v2/growth/forms/${formId}/publish`,
                method: "POST",
                data: {},
            }),
            invalidatesTags: (_result, _error, { formId }) => [
                { type: "AdminV2GrowthForms", id: "LIST" },
                { type: "AdminV2GrowthForms", id: formId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        archiveAdminV2GrowthForm: builder.mutation<
            AdminV2GrowthFormDetailResponse,
            { formId: string }
        >({
            query: ({ formId }) => ({
                url: `/admin/v2/growth/forms/${formId}/archive`,
                method: "POST",
                data: {},
            }),
            invalidatesTags: (_result, _error, { formId }) => [
                { type: "AdminV2GrowthForms", id: "LIST" },
                { type: "AdminV2GrowthForms", id: formId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2GrowthFormSubmissions: builder.query<
            AdminV2GrowthFormSubmissionsResponse,
            {
                formId: string;
                q?: string;
                status?: AdminV2GrowthFormSubmissionStatus;
                limit?: number;
                offset?: number;
            }
        >({
            query: ({ formId, ...params }) => ({
                url: `/admin/v2/growth/forms/${formId}/submissions`,
                params,
            }),
            keepUnusedDataFor: 10,
        }),

        getAdminV2GrowthTags: builder.query<
            AdminV2GrowthTagsListResponse,
            {
                organisationId?: string;
                q?: string;
                category?: string;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/growth/tags",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2GrowthTags", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        createAdminV2GrowthTag: builder.mutation<
            AdminV2GrowthTagRecordResponse,
            {
                organisationId: string;
                name: string;
                slug?: string;
                description?: string | null;
                category?: string | null;
                color?: string | null;
            }
        >({
            query: body => ({
                url: "/admin/v2/growth/tags",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2GrowthTags", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        updateAdminV2GrowthTag: builder.mutation<
            AdminV2GrowthTagRecordResponse,
            {
                tagId: string;
                body: {
                    name?: string;
                    slug?: string;
                    description?: string | null;
                    category?: string | null;
                    color?: string | null;
                };
            }
        >({
            query: ({ tagId, body }) => ({
                url: `/admin/v2/growth/tags/${tagId}`,
                method: "PUT",
                data: body,
            }),
            invalidatesTags: (_result, _error, { tagId }) => [
                { type: "AdminV2GrowthTags", id: "LIST" },
                { type: "AdminV2GrowthTags", id: tagId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        deleteAdminV2GrowthTag: builder.mutation<
            AdminV2GrowthTagDeleteResponse,
            { tagId: string }
        >({
            query: ({ tagId }) => ({
                url: `/admin/v2/growth/tags/${tagId}`,
                method: "DELETE",
            }),
            invalidatesTags: (_result, _error, { tagId }) => [
                { type: "AdminV2GrowthTags", id: "LIST" },
                { type: "AdminV2GrowthTags", id: tagId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        mergeAdminV2GrowthTags: builder.mutation<
            AdminV2GrowthTagMergeResponse,
            { sourceTagId: string; targetTagId: string }
        >({
            query: body => ({
                url: "/admin/v2/growth/tags/merge",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2GrowthTags", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        previewAdminV2GrowthTagMerge: builder.mutation<
            AdminV2GrowthTagMergePreviewResponse,
            { sourceTagId: string; targetTagId: string }
        >({
            query: body => ({
                url: "/admin/v2/growth/tags/merge-preview",
                method: "POST",
                data: body,
            }),
        }),

        previewAdminV2GrowthTagBulk: builder.mutation<
            AdminV2GrowthTagBulkPreviewResponse,
            {
                organisationId: string;
                tagId: string;
                operation: AdminV2GrowthTagBulkMode;
                personIds: string[];
            }
        >({
            query: body => ({
                url: "/admin/v2/growth/tags/bulk-preview",
                method: "POST",
                data: body,
            }),
        }),

        bulkApplyAdminV2GrowthTag: builder.mutation<
            AdminV2GrowthTagBulkJobResponse,
            { organisationId: string; tagId: string; personIds: string[] }
        >({
            query: body => ({
                url: "/admin/v2/growth/tags/bulk-apply",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2Jobs", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        bulkRemoveAdminV2GrowthTag: builder.mutation<
            AdminV2GrowthTagBulkJobResponse,
            { organisationId: string; tagId: string; personIds: string[] }
        >({
            query: body => ({
                url: "/admin/v2/growth/tags/bulk-remove",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2Jobs", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2GrowthSegments: builder.query<
            AdminV2GrowthSegmentsListResponse,
            {
                organisationId?: string;
                q?: string;
                status?: AdminV2GrowthSegmentStatus;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/growth/segments",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2GrowthSegments", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        createAdminV2GrowthSegment: builder.mutation<
            AdminV2GrowthSegmentRecordResponse,
            {
                organisationId: string;
                name: string;
                description?: string | null;
                rules: AdminV2JsonValue;
            }
        >({
            query: body => ({
                url: "/admin/v2/growth/segments",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2GrowthSegments", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2GrowthSegment: builder.query<
            AdminV2GrowthSegmentRecordResponse,
            { segmentId: string }
        >({
            query: ({ segmentId }) => ({
                url: `/admin/v2/growth/segments/${segmentId}`,
            }),
            providesTags: (_result, _error, { segmentId }) => [
                { type: "AdminV2GrowthSegments", id: segmentId },
            ],
            keepUnusedDataFor: 20,
        }),

        updateAdminV2GrowthSegment: builder.mutation<
            AdminV2GrowthSegmentRecordResponse,
            {
                segmentId: string;
                body: {
                    name?: string;
                    description?: string | null;
                    rules?: AdminV2JsonValue;
                };
            }
        >({
            query: ({ segmentId, body }) => ({
                url: `/admin/v2/growth/segments/${segmentId}`,
                method: "PUT",
                data: body,
            }),
            invalidatesTags: (_result, _error, { segmentId }) => [
                { type: "AdminV2GrowthSegments", id: "LIST" },
                { type: "AdminV2GrowthSegments", id: segmentId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        publishAdminV2GrowthSegment: builder.mutation<
            AdminV2GrowthSegmentRecordResponse,
            { segmentId: string }
        >({
            query: ({ segmentId }) => ({
                url: `/admin/v2/growth/segments/${segmentId}/publish`,
                method: "POST",
                data: {},
            }),
            invalidatesTags: (_result, _error, { segmentId }) => [
                { type: "AdminV2GrowthSegments", id: "LIST" },
                { type: "AdminV2GrowthSegments", id: segmentId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        previewAdminV2GrowthSegmentById: builder.mutation<
            AdminV2GrowthSegmentPreviewByIdResponse,
            { segmentId: string; sampleLimit?: number }
        >({
            query: ({ segmentId, ...body }) => ({
                url: `/admin/v2/growth/segments/${segmentId}/preview`,
                method: "POST",
                data: body,
            }),
        }),

        previewAdminV2GrowthSegmentRules: builder.mutation<
            AdminV2GrowthSegmentPreviewByRulesResponse,
            {
                organisationId: string;
                rules: AdminV2JsonValue;
                sampleLimit?: number;
            }
        >({
            query: body => ({
                url: "/admin/v2/growth/segments/preview",
                method: "POST",
                data: body,
            }),
        }),

        recomputeAdminV2GrowthSegment: builder.mutation<
            AdminV2GrowthSegmentRecomputeJobResponse,
            { segmentId: string }
        >({
            query: ({ segmentId }) => ({
                url: `/admin/v2/growth/segments/${segmentId}/recompute`,
                method: "POST",
                data: {},
            }),
            invalidatesTags: [
                { type: "AdminV2Jobs", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2GrowthCampaigns: builder.query<
            AdminV2GrowthCampaignsListResponse,
            {
                organisationId?: string;
                q?: string;
                status?: AdminV2GrowthCampaignStatus;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/growth/campaigns",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2GrowthCampaigns", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        createAdminV2GrowthCampaign: builder.mutation<
            AdminV2GrowthCampaignRecordResponse,
            {
                organisationId: string;
                name: string;
                description?: string | null;
                segmentId: string;
                templateId: string;
                senderIdentityId?: string | null;
                channel: AdminV2MessagingChannel;
                category: AdminV2MessagingCategory;
                metadata?: AdminV2JsonValue;
            }
        >({
            query: body => ({
                url: "/admin/v2/growth/campaigns",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2GrowthCampaigns", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2GrowthCampaign: builder.query<
            AdminV2GrowthCampaignRecordResponse,
            { campaignId: string }
        >({
            query: ({ campaignId }) => ({
                url: `/admin/v2/growth/campaigns/${campaignId}`,
            }),
            providesTags: (_result, _error, { campaignId }) => [
                { type: "AdminV2GrowthCampaigns", id: campaignId },
            ],
            keepUnusedDataFor: 20,
        }),

        getAdminV2GrowthCampaignPreview: builder.query<
            AdminV2GrowthCampaignPreviewResponse,
            { campaignId: string; sampleLimit?: number }
        >({
            query: ({ campaignId, sampleLimit }) => ({
                url: `/admin/v2/growth/campaigns/${campaignId}/preview`,
                params:
                    typeof sampleLimit === "number"
                        ? { sampleLimit: Math.max(1, Math.floor(sampleLimit)) }
                        : undefined,
            }),
            providesTags: (_result, _error, { campaignId }) => [
                { type: "AdminV2GrowthCampaigns", id: campaignId },
            ],
            keepUnusedDataFor: 0,
        }),

        updateAdminV2GrowthCampaign: builder.mutation<
            AdminV2GrowthCampaignRecordResponse,
            {
                campaignId: string;
                body: {
                    name?: string;
                    description?: string | null;
                    segmentId?: string;
                    templateId?: string;
                    senderIdentityId?: string | null;
                    channel?: AdminV2MessagingChannel;
                    category?: AdminV2MessagingCategory;
                    metadata?: AdminV2JsonValue;
                };
            }
        >({
            query: ({ campaignId, body }) => ({
                url: `/admin/v2/growth/campaigns/${campaignId}`,
                method: "PUT",
                data: body,
            }),
            invalidatesTags: (_result, _error, { campaignId }) => [
                { type: "AdminV2GrowthCampaigns", id: "LIST" },
                { type: "AdminV2GrowthCampaigns", id: campaignId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        scheduleAdminV2GrowthCampaign: builder.mutation<
            AdminV2GrowthCampaignSendResponse,
            {
                campaignId: string;
                body: {
                    scheduledAt: string;
                    approvalRequestId?: string;
                    batchSize?: number;
                };
            }
        >({
            query: ({ campaignId, body }) => ({
                url: `/admin/v2/growth/campaigns/${campaignId}/schedule`,
                method: "POST",
                data: body,
            }),
            invalidatesTags: (_result, _error, { campaignId }) => [
                { type: "AdminV2GrowthCampaigns", id: "LIST" },
                { type: "AdminV2GrowthCampaigns", id: campaignId },
                { type: "AdminV2Approvals", id: "LIST" },
                { type: "AdminV2Jobs", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        sendNowAdminV2GrowthCampaign: builder.mutation<
            AdminV2GrowthCampaignSendResponse,
            {
                campaignId: string;
                body?: {
                    approvalRequestId?: string;
                    batchSize?: number;
                };
            }
        >({
            query: ({ campaignId, body }) => ({
                url: `/admin/v2/growth/campaigns/${campaignId}/send`,
                method: "POST",
                data: body ?? {},
            }),
            invalidatesTags: (_result, _error, { campaignId }) => [
                { type: "AdminV2GrowthCampaigns", id: "LIST" },
                { type: "AdminV2GrowthCampaigns", id: campaignId },
                { type: "AdminV2Approvals", id: "LIST" },
                { type: "AdminV2Jobs", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2GrowthCampaignMetrics: builder.query<
            AdminV2GrowthCampaignMetricsResponse,
            { campaignId: string }
        >({
            query: ({ campaignId }) => ({
                url: `/admin/v2/growth/campaigns/${campaignId}/metrics`,
            }),
            providesTags: (_result, _error, { campaignId }) => [
                { type: "AdminV2GrowthCampaigns", id: campaignId },
            ],
            keepUnusedDataFor: 20,
        }),

        getAdminV2GrowthSequences: builder.query<
            AdminV2GrowthSequencesListResponse,
            {
                organisationId?: string;
                q?: string;
                status?: AdminV2GrowthSequenceStatus;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/growth/sequences",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2GrowthSequences", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        createAdminV2GrowthSequence: builder.mutation<
            AdminV2GrowthSequenceDetailResponse,
            {
                organisationId: string;
                sequenceKey?: string;
                name: string;
                description?: string | null;
                builderSchema: AdminV2JsonValue;
                stopConditions?: AdminV2JsonValue;
                metadata?: AdminV2JsonValue;
            }
        >({
            query: body => ({
                url: "/admin/v2/growth/sequences",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2GrowthSequences", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2GrowthSequence: builder.query<
            AdminV2GrowthSequenceDetailResponse,
            { sequenceId: string }
        >({
            query: ({ sequenceId }) => ({
                url: `/admin/v2/growth/sequences/${sequenceId}`,
            }),
            providesTags: (_result, _error, { sequenceId }) => [
                { type: "AdminV2GrowthSequences", id: sequenceId },
            ],
            keepUnusedDataFor: 20,
        }),

        updateAdminV2GrowthSequence: builder.mutation<
            AdminV2GrowthSequenceDetailResponse,
            {
                sequenceId: string;
                body: {
                    sequenceKey?: string;
                    name?: string;
                    description?: string | null;
                    builderSchema?: AdminV2JsonValue;
                    stopConditions?: AdminV2JsonValue;
                    metadata?: AdminV2JsonValue;
                };
            }
        >({
            query: ({ sequenceId, body }) => ({
                url: `/admin/v2/growth/sequences/${sequenceId}`,
                method: "PUT",
                data: body,
            }),
            invalidatesTags: (_result, _error, { sequenceId }) => [
                { type: "AdminV2GrowthSequences", id: "LIST" },
                { type: "AdminV2GrowthSequences", id: sequenceId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        publishAdminV2GrowthSequence: builder.mutation<
            AdminV2GrowthSequenceDetailResponse,
            { sequenceId: string }
        >({
            query: ({ sequenceId }) => ({
                url: `/admin/v2/growth/sequences/${sequenceId}/publish`,
                method: "POST",
                data: {},
            }),
            invalidatesTags: (_result, _error, { sequenceId }) => [
                { type: "AdminV2GrowthSequences", id: "LIST" },
                { type: "AdminV2GrowthSequences", id: sequenceId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        enrollAdminV2GrowthSequencePerson: builder.mutation<
            AdminV2GrowthSequenceEnrollPersonResponse,
            {
                sequenceId: string;
                body: {
                    personId: string;
                    enrollmentSource?: string;
                    idempotencyKey?: string;
                    metadata?: AdminV2JsonValue;
                };
            }
        >({
            query: ({ sequenceId, body }) => ({
                url: `/admin/v2/growth/sequences/${sequenceId}/enrollments`,
                method: "POST",
                data: body,
            }),
            invalidatesTags: (_result, _error, { sequenceId, body }) => [
                { type: "AdminV2GrowthSequences", id: sequenceId },
                { type: "AdminV2GrowthSequences", id: "LIST" },
                {
                    type: "AdminV2GrowthSequences",
                    id: `PERSON_${body.personId}`,
                },
                { type: "AdminV2Jobs", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2GrowthSequencePersonEnrollments: builder.query<
            AdminV2GrowthSequencePersonEnrollmentsResponse,
            {
                personId: string;
                organisationId: string;
                limit?: number;
                offset?: number;
            }
        >({
            query: ({ personId, ...params }) => ({
                url: `/admin/v2/growth/sequences/enrollments/people/${personId}`,
                params,
            }),
            providesTags: (_result, _error, { personId }) => [
                { type: "AdminV2GrowthSequences", id: `PERSON_${personId}` },
            ],
            keepUnusedDataFor: 20,
        }),

        unenrollAdminV2GrowthSequenceEnrollment: builder.mutation<
            AdminV2GrowthSequenceUnenrollResponse,
            {
                enrollmentId: string;
                body?: {
                    reason?: string;
                };
            }
        >({
            query: ({ enrollmentId, body }) => ({
                url: `/admin/v2/growth/sequences/enrollments/${enrollmentId}/unenroll`,
                method: "POST",
                data: body ?? {},
            }),
            invalidatesTags: (result, _error, { enrollmentId }) => [
                { type: "AdminV2GrowthSequences", id: "LIST" },
                { type: "AdminV2GrowthSequences", id: enrollmentId },
                ...(result?.enrollment.personId
                    ? [
                          {
                              type: "AdminV2GrowthSequences" as const,
                              id: `PERSON_${result.enrollment.personId}`,
                          },
                      ]
                    : []),
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2Automations: builder.query<
            AdminV2AutomationsListResponse,
            {
                organisationId?: string;
                status?: AdminV2AutomationStatus;
                q?: string;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/automations",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2Automations", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        createAdminV2Automation: builder.mutation<
            AdminV2AutomationDetailResponse,
            {
                organisationId: string;
                name: string;
                automationKey?: string;
                description?: string;
                graphSchema?: AdminV2AutomationGraphSchema;
                maxRunsPerHour?: number;
                metadata?: AdminV2JsonValue;
            }
        >({
            query: body => ({
                url: "/admin/v2/automations",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2Automations", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2Automation: builder.query<
            AdminV2AutomationDetailResponse,
            { automationId: string }
        >({
            query: ({ automationId }) => ({
                url: `/admin/v2/automations/${automationId}`,
            }),
            providesTags: (_result, _error, { automationId }) => [
                { type: "AdminV2Automations", id: automationId },
            ],
            keepUnusedDataFor: 10,
        }),

        updateAdminV2AutomationDraft: builder.mutation<
            AdminV2AutomationDetailResponse,
            {
                automationId: string;
                body: {
                    name?: string;
                    automationKey?: string;
                    description?: string | null;
                    graphSchema?: AdminV2AutomationGraphSchema;
                    maxRunsPerHour?: number;
                    metadata?: AdminV2JsonValue;
                };
            }
        >({
            query: ({ automationId, body }) => ({
                url: `/admin/v2/automations/${automationId}/draft`,
                method: "PUT",
                data: body,
            }),
            invalidatesTags: (_result, _error, { automationId }) => [
                { type: "AdminV2Automations", id: "LIST" },
                { type: "AdminV2Automations", id: automationId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        publishAdminV2Automation: builder.mutation<
            AdminV2AutomationPublishResponse,
            { automationId: string; approved?: boolean }
        >({
            query: ({ automationId, approved }) => ({
                url: `/admin/v2/automations/${automationId}/publish`,
                method: "POST",
                data: {
                    ...(approved === true ? { approved: true } : {}),
                },
            }),
            invalidatesTags: (_result, _error, { automationId }) => [
                { type: "AdminV2Automations", id: "LIST" },
                { type: "AdminV2Automations", id: automationId },
                { type: "AdminV2Automations", id: `RUNS_${automationId}` },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        archiveAdminV2Automation: builder.mutation<
            AdminV2AutomationDetailResponse,
            { automationId: string }
        >({
            query: ({ automationId }) => ({
                url: `/admin/v2/automations/${automationId}/archive`,
                method: "POST",
                data: {},
            }),
            invalidatesTags: (_result, _error, { automationId }) => [
                { type: "AdminV2Automations", id: "LIST" },
                { type: "AdminV2Automations", id: automationId },
                { type: "AdminV2Automations", id: `RUNS_${automationId}` },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        dryRunAdminV2Automation: builder.mutation<
            AdminV2AutomationDryRunResponse,
            {
                automationId: string;
                body: {
                    eventName: string;
                    aggregateType?: string;
                    aggregateId?: string;
                    payload: AdminV2JsonValue;
                    metadata?: AdminV2JsonValue;
                    correlationId?: string;
                    causationId?: string;
                };
            }
        >({
            query: ({ automationId, body }) => ({
                url: `/admin/v2/automations/${automationId}/dry-run`,
                method: "POST",
                data: body,
            }),
        }),

        getAdminV2AutomationRuns: builder.query<
            AdminV2AutomationRunsResponse,
            {
                automationId: string;
                status?: AdminV2AutomationRunStatus;
                limit?: number;
                offset?: number;
            }
        >({
            query: ({ automationId, ...params }) => ({
                url: `/admin/v2/automations/${automationId}/runs`,
                params,
            }),
            providesTags: (result, _error, { automationId }) => [
                { type: "AdminV2Automations", id: `RUNS_${automationId}` },
                ...(result?.runs ?? []).map(run => ({
                    type: "AdminV2Automations" as const,
                    id: `RUN_${run.id}`,
                })),
            ],
            keepUnusedDataFor: 10,
        }),

        getAdminV2AutomationRunDetail: builder.query<
            AdminV2AutomationRunDetailResponse,
            { runId: string }
        >({
            query: ({ runId }) => ({
                url: `/admin/v2/automations/runs/${runId}`,
            }),
            providesTags: (_result, _error, { runId }) => [
                { type: "AdminV2Automations", id: `RUN_${runId}` },
            ],
            keepUnusedDataFor: 10,
        }),

        getAdminV2SupportTickets: builder.query<
            AdminV2SupportTicketsListResponse,
            {
                q?: string;
                status?: AdminV2SupportTicketStatus;
                priority?: AdminV2SupportTicketPriority;
                assigneeStaffUserId?: string;
                linkedBusinessId?: string;
                organisationId?: string;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/support/tickets",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2SupportTickets", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        createAdminV2SupportTicket: builder.mutation<
            AdminV2SupportTicketCreateResponse,
            {
                organisationId?: string;
                subject: string;
                source?: AdminV2SupportTicketSource;
                status?: AdminV2SupportTicketStatus;
                priority?: AdminV2SupportTicketPriority;
                requesterEmail?: string;
                requesterName?: string;
                linkedPersonId?: string;
                linkedBusinessId?: string;
                linkedFirmId?: string;
                metadata?: AdminV2JsonValue;
                initialMessageMarkdown?: string;
            }
        >({
            query: body => ({
                url: "/admin/v2/support/tickets",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2SupportTickets", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2SupportTicket: builder.query<
            AdminV2SupportTicketDetailResponse,
            { ticketId: string }
        >({
            query: ({ ticketId }) => ({
                url: `/admin/v2/support/tickets/${ticketId}`,
            }),
            providesTags: (_result, _error, { ticketId }) => [
                { type: "AdminV2SupportTickets", id: ticketId },
            ],
            keepUnusedDataFor: 10,
        }),

        replyAdminV2SupportTicket: builder.mutation<
            AdminV2SupportTicketReplyResponse,
            {
                ticketId: string;
                body: {
                    bodyMarkdown: string;
                    toEmail?: string;
                    cc?: string[];
                    setPendingCustomerStatus?: boolean;
                    sendEmail?: boolean;
                };
            }
        >({
            query: ({ ticketId, body }) => ({
                url: `/admin/v2/support/tickets/${ticketId}/replies`,
                method: "POST",
                data: body,
            }),
            invalidatesTags: (_result, _error, { ticketId }) => [
                { type: "AdminV2SupportTickets", id: "LIST" },
                { type: "AdminV2SupportTickets", id: ticketId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        addAdminV2SupportTicketInternalNote: builder.mutation<
            AdminV2SupportTicketInternalNoteResponse,
            {
                ticketId: string;
                body: {
                    bodyMarkdown: string;
                };
            }
        >({
            query: ({ ticketId, body }) => ({
                url: `/admin/v2/support/tickets/${ticketId}/internal-notes`,
                method: "POST",
                data: body,
            }),
            invalidatesTags: (_result, _error, { ticketId }) => [
                { type: "AdminV2SupportTickets", id: "LIST" },
                { type: "AdminV2SupportTickets", id: ticketId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        assignAdminV2SupportTicket: builder.mutation<
            AdminV2SupportTicketAssignResponse,
            {
                ticketId: string;
                assigneeStaffUserId: string | null;
            }
        >({
            query: ({ ticketId, assigneeStaffUserId }) => ({
                url: `/admin/v2/support/tickets/${ticketId}/assign`,
                method: "POST",
                data: {
                    assigneeStaffUserId,
                },
            }),
            invalidatesTags: (_result, _error, { ticketId }) => [
                { type: "AdminV2SupportTickets", id: "LIST" },
                { type: "AdminV2SupportTickets", id: ticketId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        updateAdminV2SupportTicketStatus: builder.mutation<
            AdminV2SupportTicketStatusResponse,
            {
                ticketId: string;
                status: AdminV2SupportTicketStatus;
            }
        >({
            query: ({ ticketId, status }) => ({
                url: `/admin/v2/support/tickets/${ticketId}/status`,
                method: "POST",
                data: {
                    status,
                },
            }),
            invalidatesTags: (_result, _error, { ticketId }) => [
                { type: "AdminV2SupportTickets", id: "LIST" },
                { type: "AdminV2SupportTickets", id: ticketId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2SupportTicketSuggestedArticles: builder.mutation<
            AdminV2SupportTicketSuggestedArticlesResponse,
            {
                ticketId: string;
                body?: {
                    limit?: number;
                };
            }
        >({
            query: ({ ticketId, body }) => ({
                url: `/admin/v2/support/tickets/${ticketId}/suggested-articles`,
                method: "POST",
                data: body ?? {},
            }),
        }),

        getAdminV2HelpCollections: builder.query<
            AdminV2HelpCollectionsListResponse,
            {
                q?: string;
                organisationId?: string;
                includeGlobal?: boolean;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/support/help-collections",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2HelpCollections", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        createAdminV2HelpCollection: builder.mutation<
            AdminV2HelpCollectionRecordResponse,
            {
                organisationId?: string;
                slug: string;
                name: string;
                description?: string | null;
                sortOrder?: number;
                metadata?: AdminV2JsonValue;
            }
        >({
            query: body => ({
                url: "/admin/v2/support/help-collections",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2HelpCollections", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        updateAdminV2HelpCollection: builder.mutation<
            AdminV2HelpCollectionUpdateResponse,
            {
                collectionId: string;
                body: {
                    slug?: string;
                    name?: string;
                    description?: string | null;
                    sortOrder?: number;
                    isActive?: boolean;
                    metadata?: AdminV2JsonValue;
                };
            }
        >({
            query: ({ collectionId, body }) => ({
                url: `/admin/v2/support/help-collections/${collectionId}`,
                method: "PUT",
                data: body,
            }),
            invalidatesTags: (_result, _error, { collectionId }) => [
                { type: "AdminV2HelpCollections", id: "LIST" },
                { type: "AdminV2HelpCollections", id: collectionId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2HelpArticles: builder.query<
            AdminV2HelpArticlesListResponse,
            {
                q?: string;
                status?: AdminV2HelpArticleStatus;
                collectionId?: string;
                organisationId?: string;
                includeGlobal?: boolean;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/support/help-articles",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2HelpArticles", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        createAdminV2HelpArticle: builder.mutation<
            AdminV2HelpArticleDetailResponse,
            {
                organisationId?: string;
                collectionId?: string;
                slug: string;
                title: string;
                summary?: string | null;
                bodyMarkdown: string;
                metadata?: AdminV2JsonValue;
            }
        >({
            query: body => ({
                url: "/admin/v2/support/help-articles",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2HelpArticles", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminV2HelpArticle: builder.query<
            AdminV2HelpArticleDetailResponse,
            { articleId: string }
        >({
            query: ({ articleId }) => ({
                url: `/admin/v2/support/help-articles/${articleId}`,
            }),
            providesTags: (_result, _error, { articleId }) => [
                { type: "AdminV2HelpArticles", id: articleId },
            ],
            keepUnusedDataFor: 10,
        }),

        updateAdminV2HelpArticle: builder.mutation<
            AdminV2HelpArticleUpdateResponse,
            {
                articleId: string;
                body: {
                    slug?: string;
                    title?: string;
                    summary?: string | null;
                    bodyMarkdown?: string;
                    collectionId?: string | null;
                    metadata?: AdminV2JsonValue;
                };
            }
        >({
            query: ({ articleId, body }) => ({
                url: `/admin/v2/support/help-articles/${articleId}`,
                method: "PUT",
                data: body,
            }),
            invalidatesTags: (_result, _error, { articleId }) => [
                { type: "AdminV2HelpArticles", id: "LIST" },
                { type: "AdminV2HelpArticles", id: articleId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        publishAdminV2HelpArticle: builder.mutation<
            AdminV2HelpArticleUpdateResponse,
            {
                articleId: string;
            }
        >({
            query: ({ articleId }) => ({
                url: `/admin/v2/support/help-articles/${articleId}/publish`,
                method: "POST",
                data: {},
            }),
            invalidatesTags: (_result, _error, { articleId }) => [
                { type: "AdminV2HelpArticles", id: "LIST" },
                { type: "AdminV2HelpArticles", id: articleId },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        searchAdminV2HelpArticles: builder.mutation<
            AdminV2HelpArticleSearchResponse,
            {
                query: string;
                organisationId?: string;
                limit?: number;
                metadata?: AdminV2JsonValue;
            }
        >({
            query: body => ({
                url: "/admin/v2/support/help-search",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [{ type: "AdminV2HelpSearchLogs", id: "LIST" }],
        }),

        getAdminV2HelpSearchLogs: builder.query<
            AdminV2HelpSearchLogsResponse,
            {
                q?: string;
                source?: string;
                organisationId?: string;
                includeGlobal?: boolean;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/support/help-search-logs",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2HelpSearchLogs", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        getAdminV2HelpFeedback: builder.query<
            AdminV2HelpFeedbackListResponse,
            {
                articleId?: string;
                isHelpful?: boolean;
                organisationId?: string;
                includeGlobal?: boolean;
                limit?: number;
                offset?: number;
            } | void
        >({
            query: params => ({
                url: "/admin/v2/support/help-feedback",
                params: params ?? undefined,
            }),
            providesTags: [{ type: "AdminV2HelpFeedback", id: "LIST" }],
            keepUnusedDataFor: 20,
        }),

        createAdminV2HelpFeedback: builder.mutation<
            AdminV2HelpFeedbackRecordResponse,
            {
                articleId: string;
                isHelpful: boolean;
                organisationId?: string;
                ticketId?: string;
                comment?: string;
                source?: string;
                metadata?: AdminV2JsonValue;
                submittedByEmail?: string;
                submittedByName?: string;
            }
        >({
            query: body => ({
                url: "/admin/v2/support/help-feedback",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "AdminV2HelpFeedback", id: "LIST" },
                { type: "AdminV2AuditLog", id: "LIST" },
            ],
        }),

        getAdminPartners: builder.query<
            AdminPartnerSummary[],
            { search?: string; limit?: number } | void
        >({
            query: args => ({
                url: "/admin/partners",
                params: args ?? undefined,
            }),
            keepUnusedDataFor: 60,
        }),
        getAdminPartner: builder.query<AdminPartnerDetail, string>({
            query: partnerId => ({
                url: `/admin/partners/${partnerId}`,
            }),
            keepUnusedDataFor: 60,
        }),
        updateAdminPartnerEntitlements: builder.mutation<
            AdminPartnerEntitlementsResponse,
            {
                partnerId: string;
                clientLifetimeLimit?: number;
                clientLimit?: number;
            }
        >({
            query: ({ partnerId, ...body }) => ({
                url: `/admin/partners/${partnerId}/entitlements`,
                method: "POST",
                data: body,
            }),
        }),
        updateAdminPartnerDefaultOrganisation: builder.mutation<
            AdminPartnerDefaultOrganisationResponse,
            { partnerId: string; defaultOrganisationId: string | null }
        >({
            query: ({ partnerId, ...body }) => ({
                url: `/admin/partners/${partnerId}/default-organisation`,
                method: "POST",
                data: body,
            }),
        }),
        transferAdminPartnerClients: builder.mutation<
            AdminPartnerTransferResponse,
            {
                partnerId: string;
                targetPartnerId: string;
                organisationIds: string[];
            }
        >({
            query: ({ partnerId, ...body }) => ({
                url: `/admin/partners/${partnerId}/transfer-clients`,
                method: "POST",
                data: body,
            }),
        }),
        unlinkAdminPartnerClient: builder.mutation<
            AdminPartnerUnlinkResponse,
            { partnerId: string; organisationId: string }
        >({
            query: ({ partnerId, organisationId }) => ({
                url: `/admin/partners/${partnerId}/linked/${organisationId}`,
                method: "DELETE",
            }),
        }),

        getIndustryBreakdown: builder.query<IndustryBreakdownItem[], void>({
            query: () => ({
                url: "/admin/industry-breakdown",
            }),
            providesTags: ["SyncData"],
            transformResponse: (res: unknown): IndustryBreakdownItem[] => {
                if (!Array.isArray(res)) {
                    return [];
                }

                return res
                    .map(candidate => {
                        if (!candidate || typeof candidate !== "object") {
                            return null;
                        }

                        const raw = candidate as Record<string, unknown>;
                        const name =
                            typeof raw.name === "string" ? raw.name : null;
                        const value = coerceNumber(raw.value);

                        if (!name) {
                            return null;
                        }

                        return { name, value };
                    })
                    .filter(
                        (item): item is IndustryBreakdownItem => item !== null,
                    );
            },
            keepUnusedDataFor: 300,
        }),

        // Stripe Subscription Management Endpoints

        // Get all Stripe subscriptions
        getStripeSubscriptions: builder.query<
            StripeApiResponse<StripeSubscription>,
            {
                limit?: number;
                starting_after?: string;
                status?: string;
                customer?: string;
            }
        >({
            query: params => ({
                url: "/admin/stripe/subscriptions",
                params,
            }),
            providesTags: result =>
                result?.data
                    ? [
                          ...result.data.map(({ id }) => ({
                              type: "StripeSubscription" as const,
                              id,
                          })),
                          { type: "StripeSubscription", id: "LIST" },
                      ]
                    : [{ type: "StripeSubscription", id: "LIST" }],
            // Subscriptions change frequently
            keepUnusedDataFor: 120, // 2 minutes
        }),

        // Get active Stripe products and prices
        getStripeActiveProducts: builder.query<
            {
                products: StripeApiResponse<StripeProduct>;
                prices: StripeApiResponse<StripePrice>;
            },
            void
        >({
            query: () => ({
                url: "/admin/stripe/activeProducts",
            }),
            providesTags: [
                { type: "StripeProduct", id: "LIST" },
                { type: "StripePrice", id: "LIST" },
            ],
            // Products and prices change less frequently
            keepUnusedDataFor: 600, // 10 minutes
        }),

        // Create checkout session (for testing purposes)
        createCheckoutSession: builder.mutation<
            CheckoutSessionResponse,
            CreateCheckoutSessionRequest
        >({
            query: body => ({
                url: "/admin/stripe/checkout",
                method: "POST",
                data: body,
            }),
            // No cache invalidation needed for checkout sessions
        }),

        createManualCheckoutLink: builder.mutation<
            {
                id: string;
                url: string | null;
                expiresAt: number | null;
                stripeCustomerId: string;
                mode: string;
            },
            {
                organisationId: string;
                firebaseUserId: string;
                productId: string;
                successUrl?: string;
                cancelUrl?: string;
                metadata?: Record<string, string>;
            }
        >({
            query: body => ({
                url: "/admin/stripe/manual-checkout",
                method: "POST",
                data: body,
            }),
        }),

        // Create customer portal session
        createPortalSession: builder.mutation<
            PortalSessionResponse,
            CreatePortalSessionRequest
        >({
            query: body => ({
                url: "/admin/stripe/portal",
                method: "POST",
                data: body,
            }),
            // No cache invalidation needed for portal sessions
        }),

        // Get subscription statistics for dashboard
        getSubscriptionStatistics: builder.query<
            SubscriptionStatisticsApiResponse,
            void
        >({
            query: () => ({
                url: "/admin/stripe/statistics",
            }),
            providesTags: ["SyncData"],
            // Statistics change less frequently
            keepUnusedDataFor: 300, // 5 minutes
        }),

        // New Product-Grouped Endpoints

        // Get products with grouped prices and primary price configuration
        getProductsWithPrices: builder.query<ProductWithPrices[], void>({
            query: () => ({
                url: "/admin/stripe/products-with-prices",
            }),
            providesTags: [
                { type: "StripeProduct", id: "LIST" },
                { type: "StripePrice", id: "LIST" },
            ],
            // Products and prices change less frequently
            keepUnusedDataFor: 600, // 10 minutes
        }),

        getStripeDiscounts: builder.query<StripeDiscountsResponse, void>({
            query: () => ({
                url: "/admin/stripe/discounts",
            }),
            // Discounts change less frequently; this is primarily for admin UX.
            keepUnusedDataFor: 600, // 10 minutes
        }),

        // Set primary price for a product
        setPrimaryPrice: builder.mutation<
            SetPrimaryPriceResponse,
            SetPrimaryPriceRequest
        >({
            query: body => ({
                url: "/admin/stripe/set-primary-price",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [
                { type: "StripeProduct", id: "LIST" },
                { type: "StripePrice", id: "LIST" },
            ],
        }),

        // Create checkout session using product ID (resolves to primary price)
        createProductCheckoutSession: builder.mutation<
            CheckoutSessionResponse,
            ProductOnboardingRequest
        >({
            query: body => ({
                url: "/admin/stripe/product-checkout",
                method: "POST",
                data: body,
            }),
            // No cache invalidation needed for checkout sessions
        }),

        // Get subscription objects for a specific organisation
        getStripeSubscriptionObjects: builder.query<
            StripeSubscriptionObjectsResponse,
            string
        >({
            query: organisationId => ({
                url: `/admin/stripe/subscription-objects/${organisationId}`,
            }),
            providesTags: (_result, _error, organisationId) => [
                { type: "StripeSubscriptionObjects", id: organisationId },
            ],
            // Subscription objects change frequently
            keepUnusedDataFor: 120, // 2 minutes
        }),

        // --- Admin subscription tabs (Stripe Audit + Manual Adjustments) ---
        getAdminSubscriptionLocalSnapshot: builder.query<
            AdminSubscriptionLocalSnapshot,
            string
        >({
            query: orgId => ({
                url: `/admin/subscriptions/${orgId}/audit/local`,
            }),
            providesTags: (_result, _error, orgId) => [
                { type: "AdminSubscriptionLocalSnapshot", id: orgId },
            ],
            keepUnusedDataFor: 60,
        }),

        createAdminSubscriptionAuditRun: builder.mutation<
            CreateAuditRunResponse,
            { orgId: string }
        >({
            query: ({ orgId }) => ({
                url: `/admin/subscriptions/${orgId}/audit/runs`,
                method: "POST",
            }),
            invalidatesTags: (_result, _error, { orgId }) => [
                { type: "AdminSubscriptionLocalSnapshot", id: orgId },
                { type: "AdminSubscriptionAuditRun", id: orgId },
            ],
        }),

        getAdminSubscriptionLatestAuditRun: builder.query<
            SubscriptionAuditRun | null,
            string
        >({
            query: orgId => ({
                url: `/admin/subscriptions/${orgId}/audit/runs/latest`,
            }),
            providesTags: (_result, _error, orgId) => [
                { type: "AdminSubscriptionAuditRun", id: orgId },
            ],
            keepUnusedDataFor: 30,
        }),

        getAdminSubscriptionAuditRun: builder.query<
            SubscriptionAuditRun,
            { orgId: string; runId: string }
        >({
            query: ({ orgId, runId }) => ({
                url: `/admin/subscriptions/${orgId}/audit/runs/${runId}`,
            }),
            providesTags: (_result, _error, { runId }) => [
                { type: "AdminSubscriptionAuditRun", id: runId },
            ],
            keepUnusedDataFor: 0,
        }),

        reconcileAdminSubscriptionFromStripe: builder.mutation<
            ReconcileFromStripeResponse,
            {
                orgId: string;
                reason: string;
                ticketUrl?: string;
                actionId?: string;
            }
        >({
            query: ({ orgId, ...body }) => ({
                url: `/admin/subscriptions/${orgId}/audit/repairs/reconcile-from-stripe`,
                method: "POST",
                data: body,
            }),
            invalidatesTags: (_result, _error, { orgId }) => [
                { type: "AdminSubscriptionLocalSnapshot", id: orgId },
                { type: "AdminSubscriptionStripeSummary", id: orgId },
            ],
        }),

        rebuildAdminSubscriptionSnapshots: builder.mutation<
            RebuildSnapshotsResponse,
            {
                orgId: string;
                reason: string;
                ticketUrl?: string;
                actionId?: string;
            }
        >({
            query: ({ orgId, ...body }) => ({
                url: `/admin/subscriptions/${orgId}/audit/repairs/rebuild-snapshots`,
                method: "POST",
                data: body,
            }),
            invalidatesTags: (_result, _error, { orgId }) => [
                { type: "AdminSubscriptionLocalSnapshot", id: orgId },
            ],
        }),

        fixAdminSubscriptionMetadata: builder.mutation<
            FixSubscriptionMetadataResponse,
            {
                orgId: string;
                subscriptionId: string;
                reason: string;
                ticketUrl?: string;
                actionId?: string;
            }
        >({
            query: ({ orgId, ...body }) => ({
                url: `/admin/subscriptions/${orgId}/audit/repairs/fix-subscription-metadata`,
                method: "POST",
                data: body,
            }),
            invalidatesTags: (_result, _error, { orgId }) => [
                { type: "AdminSubscriptionLocalSnapshot", id: orgId },
                { type: "AdminSubscriptionStripeSummary", id: orgId },
            ],
        }),

        getAdminSubscriptionStripeSummary: builder.query<
            StripeSubscriptionSummary | null,
            { orgId: string; subscriptionId?: string }
        >({
            query: ({ orgId, subscriptionId }) => ({
                url: `/admin/subscriptions/${orgId}/stripe/summary`,
                params: subscriptionId ? { subscriptionId } : undefined,
            }),
            providesTags: (_result, _error, { orgId }) => [
                { type: "AdminSubscriptionStripeSummary", id: orgId },
            ],
            keepUnusedDataFor: 0,
        }),

        previewAdminSubscriptionInvoice: builder.mutation<
            StripeInvoicePreviewSummary,
            {
                orgId: string;
                subscriptionId: string;
                newPriceId?: string;
                newQuantity?: number;
            }
        >({
            query: ({ orgId, ...body }) => ({
                url: `/admin/subscriptions/${orgId}/actions/preview-invoice`,
                method: "POST",
                data: body,
            }),
        }),

        listAdminSubscriptionActions: builder.query<
            AdminSubscriptionAction[],
            { orgId: string; limit?: number }
        >({
            query: ({ orgId, limit }) => ({
                url: `/admin/subscriptions/${orgId}/actions`,
                params: limit ? { limit } : undefined,
            }),
            providesTags: (_result, _error, { orgId }) => [
                { type: "AdminSubscriptionAction", id: orgId },
            ],
            keepUnusedDataFor: 30,
        }),

        executeAdminSubscriptionAction: builder.mutation<
            ExecuteAdminSubscriptionActionResponse,
            {
                orgId: string;
                actionType: string;
                reason: string;
                ticketUrl?: string;
                actionId?: string;
                payload?: JsonValue;
            }
        >({
            query: ({ orgId, ...body }) => ({
                url: `/admin/subscriptions/${orgId}/actions`,
                method: "POST",
                data: body,
            }),
            invalidatesTags: (_result, _error, { orgId }) => [
                { type: "AdminSubscriptionLocalSnapshot", id: orgId },
                { type: "AdminSubscriptionAction", id: orgId },
                { type: "AdminSubscriptionStripeSummary", id: orgId },
            ],
        }),

        // Get users with Firebase custom claims
        getUsersWithClaims: builder.query<
            AdminUserWithClaimsResponse,
            { firebaseClaimKey: string }
        >({
            query: ({ firebaseClaimKey }) => ({
                url: "/admin/users-with-claims",
                params: { firebaseClaimKey },
            }),
            providesTags: [{ type: "AdminUserWithClaims", id: "LIST" }],
            // Claims data changes less frequently
            keepUnusedDataFor: 300, // 5 minutes
        }),

        // Grant free subscription to an organisation
        grantFreeSubscription: builder.mutation<
            {
                success: boolean;
                subscriptionId?: string;
                error?: string;
                details?: {
                    organisationId: string;
                    stripeCustomerId: string;
                    priceId: string;
                    couponId: string;
                    grantedAt: string;
                };
            },
            {
                organisationId: string;
                reason: string;
                actionId?: string;
                duration?: "forever" | "once" | "repeating";
                durationInMonths?: number;
                productId?: string;
            }
        >({
            query: body => ({
                url: "/admin/grant-free-subscription",
                method: "POST",
                data: body,
            }),
            invalidatesTags: (_result, _error, { organisationId }) => [
                { type: "Organisation", id: organisationId },
                { type: "Organisation", id: "LIST" },
                { type: "StripeSubscriptionObjects", id: organisationId },
            ],
        }),

        // Revoke free subscription
        revokeFreeSubscription: builder.mutation<
            {
                success: boolean;
                error?: string;
            },
            {
                subscriptionId: string;
                reason: string;
                actionId?: string;
                organisationId?: string; // Optional for better cache invalidation
            }
        >({
            query: body => ({
                url: "/admin/revoke-free-subscription",
                method: "POST",
                data: body,
            }),
            invalidatesTags: (_result, _error, { organisationId }) => {
                const tags: Array<{
                    type: "Organisation" | "StripeSubscriptionObjects";
                    id: string;
                }> = [{ type: "Organisation", id: "LIST" }];

                if (organisationId) {
                    // If we have the organisation ID, invalidate specific subscription objects
                    tags.push({
                        type: "StripeSubscriptionObjects",
                        id: organisationId,
                    });
                } else {
                    // Otherwise, invalidate all subscription objects
                    tags.push({
                        type: "StripeSubscriptionObjects",
                        id: "LIST",
                    });
                }

                return tags;
            },
        }),

        // Remove claim from user
        removeClaimFromUser: builder.mutation<
            {
                success: boolean;
                message?: string;
                error?: string;
            },
            {
                uid: string;
                claimKey: string;
            }
        >({
            query: body => ({
                url: "/admin/remove-claim-from-user",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [{ type: "AdminUserWithClaims", id: "LIST" }],
        }),

        // Disconnect organisation
        disconnectOrganisation: builder.mutation<
            {
                success: boolean;
                message?: string;
                error?: string;
            },
            {
                organisationId: string;
                reason?: string;
            }
        >({
            query: body => ({
                url: "/admin/disconnect-organisation",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [{ type: "Organisation", id: "LIST" }],
        }),

        // Get usage analytics metrics - admin operation
        // @see docs/features/USAGE_TRACKING.md
        getUsageAnalytics: builder.query<
            UsageAnalyticsResponse,
            UsageAnalyticsFilters | void
        >({
            query: (filters = {}) => ({
                url: "/admin/usage-metrics",
                params: filters,
            }),
            providesTags: [{ type: "UsageMetrics", id: "LIST" }],
            keepUnusedDataFor: 60, // Refresh frequently for near-real-time dashboards
        }),

        // Demo Access Management Endpoints

        // Get all demo access assignments
        getDemoAccessAssignments: builder.query<DemoAccessAssignment[], void>({
            query: () => ({
                url: "/admin/demo-access",
            }),
            providesTags: [{ type: "DemoAccess", id: "LIST" }],
            // Demo access assignments change infrequently
            keepUnusedDataFor: 300, // 5 minutes
        }),

        // Grant demo access to a user
        grantDemoAccess: builder.mutation<
            GrantDemoAccessResponse,
            GrantDemoAccessRequest
        >({
            query: body => ({
                url: "/admin/demo-access",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [{ type: "DemoAccess", id: "LIST" }],
        }),

        // Revoke demo access from a user
        revokeDemoAccess: builder.mutation<
            DemoAccessAssignment,
            RevokeDemoAccessRequest
        >({
            query: ({ firebaseId }) => ({
                url: `/admin/demo-access/${firebaseId}`,
                method: "DELETE",
            }),
            invalidatesTags: [{ type: "DemoAccess", id: "LIST" }],
        }),

        // Call Transcript Intake (admin) endpoints
        processTranscriptIntake: builder.mutation<
            TranscriptIntakeProcessResponse,
            TranscriptIntakeProcessRequest
        >({
            query: body => ({
                url: "/admin/transcript-intake/process",
                method: "POST",
                data: body,
            }),
        }),

        getTranscriptIntakeJob: builder.query<
            TranscriptIntakeJobResponse,
            string
        >({
            query: jobId => ({
                url: `/admin/transcript-intake/jobs/${jobId}`,
            }),
            keepUnusedDataFor: 15,
        }),

        cancelTranscriptIntakeJob: builder.mutation<
            TranscriptIntakeCancelResponse,
            string
        >({
            query: jobId => ({
                url: `/admin/transcript-intake/jobs/${jobId}/cancel`,
                method: "POST",
            }),
        }),

        createTranscriptIntakeItems: builder.mutation<
            TranscriptIntakeCreateResponse,
            { jobId: string; body: TranscriptIntakeCreateRequest }
        >({
            query: ({ jobId, body }) => ({
                url: `/admin/transcript-intake/jobs/${jobId}/create`,
                method: "POST",
                data: body,
            }),
        }),

        getAdminDocsSearch: builder.query<
            AdminDocsSearchResponse,
            { query: string; limit?: number; includeArchived?: boolean }
        >({
            query: ({ query, limit, includeArchived }) => ({
                url: "/admin/docs/search",
                params: {
                    q: query,
                    ...(typeof limit === "number" ? { limit } : {}),
                    ...(includeArchived ? { includeArchived: true } : {}),
                },
            }),
            keepUnusedDataFor: 30,
        }),

        createAdminDocsShareLink: builder.mutation<
            CreateDocsShareLinkResponse,
            CreateDocsShareLinkRequest
        >({
            query: body => ({
                url: "/admin/docs/share-links",
                method: "POST",
                data: body,
            }),
            invalidatesTags: [{ type: "DocsShareLink", id: "LIST" }],
        }),

        getAdminDocsShareLinks: builder.query<
            AdminDocsShareLinksResponse,
            { status?: DocsShareStatusFilter; limit?: number } | void
        >({
            query: args => ({
                url: "/admin/docs/share-links",
                params: {
                    ...(args?.status ? { status: args.status } : {}),
                    ...(typeof args?.limit === "number"
                        ? { limit: args.limit }
                        : {}),
                },
            }),
            providesTags: result =>
                result?.links
                    ? [
                          { type: "DocsShareLink", id: "LIST" },
                          ...result.links.map(link => ({
                              type: "DocsShareLink" as const,
                              id: link.id,
                          })),
                      ]
                    : [{ type: "DocsShareLink", id: "LIST" }],
            keepUnusedDataFor: 30,
        }),

        revokeAdminDocsShareLink: builder.mutation<
            RevokeDocsShareLinkResponse,
            { id: string }
        >({
            query: ({ id }) => ({
                url: `/admin/docs/share-links/${id}/revoke`,
                method: "POST",
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: "DocsShareLink", id },
                { type: "DocsShareLink", id: "LIST" },
            ],
        }),
    }),
});

// Export the helper function for use in endpoint definitions
export { providesList };

// Export hooks for the consolidated endpoints
export const {
    useGetLogsQuery,
    useGetSpoofSessionsQuery,
    useGetQuickbooksTrialsQuery,
    useGetBulkEmailJobsQuery,
    useGetOrganisationsQuery,
    useRemoveOrganisationUserMutation,
    useGetUsersQuery,
    useGetProviderConnectionOrganisationsQuery,
    useGetDashboardStatisticsQuery,
    useGetAdminMetricsQuery,
    useGetAdminV2MeQuery,
    useGetAdminV2RevenueAccessContextQuery,
    useGetAdminV2RevenueTransactionsQuery,
    useGetAdminV2RevenueTransactionDetailQuery,
    useGetAdminV2RevenueSubscriptionsQuery,
    useGetAdminV2RevenueDisputesQuery,
    useGetAdminV2RevenueDisputeDetailQuery,
    useGetAdminV2RevenueWebhookEventsQuery,
    useGetAdminV2RevenueWebhookEventDetailQuery,
    useGetAdminV2RevenueOffersQuery,
    useGetAdminV2RevenueOfferQuery,
    useCreateAdminV2RevenueOfferMutation,
    useUpdateAdminV2RevenueOfferMutation,
    usePublishAdminV2RevenueOfferMutation,
    useGetAdminV2RevenueOnboardingFlowsQuery,
    useGetAdminV2RevenueOnboardingFlowQuery,
    useCreateAdminV2RevenueOnboardingFlowMutation,
    useUpdateAdminV2RevenueOnboardingFlowMutation,
    usePublishAdminV2RevenueOnboardingFlowMutation,
    useGetAdminV2RevenueCouponsQuery,
    useUpsertAdminV2RevenueCouponMutation,
    useGetAdminV2RevenueAffiliatesQuery,
    useUpsertAdminV2RevenueAffiliateMutation,
    useCreateAdminV2RevenueAffiliateAttributionMutation,
    useCreateAdminV2RevenueAffiliatePayoutMutation,
    useGetAdminV2LearningContentsQuery,
    useGetAdminV2LearningContentQuery,
    useCreateAdminV2LearningContentMutation,
    useUpdateAdminV2LearningContentMutation,
    usePublishAdminV2LearningContentMutation,
    useGetAdminV2LearningPathwaysQuery,
    useGetAdminV2LearningPathwayQuery,
    useCreateAdminV2LearningPathwayMutation,
    useUpdateAdminV2LearningPathwayMutation,
    usePublishAdminV2LearningPathwayMutation,
    useAssignAdminV2LearningPathwayMutation,
    useGetAdminV2LearningAssignmentsQuery,
    useGetAdminV2LearningProgressQuery,
    useUpdateAdminV2LearningProgressMutation,
    useGetAdminV2LearningScoreRuleVersionsQuery,
    useCreateAdminV2LearningScoreRuleVersionMutation,
    usePublishAdminV2LearningScoreRuleVersionMutation,
    useGetAdminV2LearningPeopleScoreDashboardQuery,
    useGetAdminV2LearningBusinessScoreDashboardQuery,
    useGetAdminV2LearningScoreTimelineQuery,
    useGetAdminV2LearningScoreExplainabilityQuery,
    useRecomputeAdminV2LearningScoreMutation,
    useGetAdminV2SalesDealsQuery,
    useCreateAdminV2SalesDealMutation,
    useUpdateAdminV2SalesDealStageMutation,
    useClaimAdminV2SalesDealMutation,
    useAddAdminV2SalesDealNoteMutation,
    useGetAdminV2SalesDealEventsQuery,
    useGetAdminV2SalesLeadScoresQuery,
    useGetAdminV2SalesMemberScoresQuery,
    useRecomputeAdminV2SalesScoresMutation,
    useGetAdminV2SalesWorkQueuesQuery,
    useGetAdminV2SalesReportingSummaryQuery,
    useGetAdminV2IntegrationsQuery,
    useUpsertAdminV2IntegrationConnectionMutation,
    usePatchAdminV2IntegrationConnectionMutation,
    useGetAdminV2IntegrationConnectionHealthQuery,
    useGetAdminV2PosthogDashboardsQuery,
    useGetAdminV2EventTaxonomyQuery,
    usePatchAdminV2EventTaxonomyMutation,
    useGetAdminV2OpsWhatNeedsAttentionQuery,
    useGetAdminV2OpsIngestFailuresQuery,
    useGetAdminV2OpsDeliverabilityHealthQuery,
    useGetAdminV2OpsSupportBacklogQuery,
    useGetAdminV2FeatureFlagsQuery,
    useUpsertAdminV2FeatureFlagMutation,
    useGetAdminV2EntitlementsQuery,
    useUpsertAdminV2EntitlementMutation,
    useGetAdminV2RbacStaffUsersQuery,
    useGetAdminV2RbacRolesQuery,
    useGetAdminV2RbacPermissionsQuery,
    useAssignAdminV2StaffRoleMutation,
    useSetAdminV2StaffPermissionOverrideMutation,
    useSearchAdminV2Query,
    useLazySearchAdminV2Query,
    useGetAdminV2ApprovalsQuery,
    useCreateAdminV2ApprovalMutation,
    useApproveAdminV2ApprovalMutation,
    useRejectAdminV2ApprovalMutation,
    useCancelAdminV2ApprovalMutation,
    useGetAdminV2AuditLogQuery,
    useGetAdminV2JobsQuery,
    useGetAdminV2JobQuery,
    useCancelAdminV2JobMutation,
    useGetAdminV2CrmPeopleQuery,
    useBulkUpdateAdminV2CrmPeopleLifecycleStageMutation,
    useGetAdminV2CrmPersonRecordQuery,
    useUpdateAdminV2CrmPersonMutation,
    useGetAdminV2CrmBusinessesQuery,
    useGetAdminV2CrmBusinessRecordQuery,
    useGetAdminV2CrmFirmsQuery,
    useGetAdminV2CrmFirmRecordQuery,
    useGetAdminV2CrmFirmClientRelationshipsQuery,
    useGetAdminV2CrmFirmClientRelationshipRecordQuery,
    useUpdateAdminV2CrmFirmClientRelationshipMutation,
    useGetAdminV2CrmTimelineQuery,
    useCreateAdminV2CrmImportRunMutation,
    useGetAdminV2CrmImportRunQuery,
    useGetAdminV2MessagingSenderIdentitiesQuery,
    useCreateAdminV2MessagingSenderIdentityMutation,
    useUpdateAdminV2MessagingSenderIdentityMutation,
    useArchiveAdminV2MessagingSenderIdentityMutation,
    useGetAdminV2MessagingTemplatesQuery,
    useGetAdminV2MessagingTemplateQuery,
    useCreateAdminV2MessagingTemplateMutation,
    useUpdateAdminV2MessagingTemplateMutation,
    usePublishAdminV2MessagingTemplateMutation,
    useArchiveAdminV2MessagingTemplateMutation,
    usePreviewAdminV2MessagingTemplateMutation,
    useGetAdminV2MessagingPersonPreferencesQuery,
    useGetAdminV2MessagingPersonInAppMessagesQuery,
    useMarkAdminV2MessagingInAppMessageReadMutation,
    useGetAdminV2GrowthFormsQuery,
    useCreateAdminV2GrowthFormMutation,
    useGetAdminV2GrowthFormQuery,
    useUpdateAdminV2GrowthFormMutation,
    usePublishAdminV2GrowthFormMutation,
    useArchiveAdminV2GrowthFormMutation,
    useGetAdminV2GrowthFormSubmissionsQuery,
    useGetAdminV2GrowthTagsQuery,
    useCreateAdminV2GrowthTagMutation,
    useUpdateAdminV2GrowthTagMutation,
    useDeleteAdminV2GrowthTagMutation,
    useMergeAdminV2GrowthTagsMutation,
    usePreviewAdminV2GrowthTagMergeMutation,
    usePreviewAdminV2GrowthTagBulkMutation,
    useBulkApplyAdminV2GrowthTagMutation,
    useBulkRemoveAdminV2GrowthTagMutation,
    useGetAdminV2GrowthSegmentsQuery,
    useCreateAdminV2GrowthSegmentMutation,
    useGetAdminV2GrowthSegmentQuery,
    useUpdateAdminV2GrowthSegmentMutation,
    usePublishAdminV2GrowthSegmentMutation,
    usePreviewAdminV2GrowthSegmentByIdMutation,
    usePreviewAdminV2GrowthSegmentRulesMutation,
    useRecomputeAdminV2GrowthSegmentMutation,
    useGetAdminV2GrowthCampaignsQuery,
    useCreateAdminV2GrowthCampaignMutation,
    useGetAdminV2GrowthCampaignQuery,
    useGetAdminV2GrowthCampaignPreviewQuery,
    useUpdateAdminV2GrowthCampaignMutation,
    useScheduleAdminV2GrowthCampaignMutation,
    useSendNowAdminV2GrowthCampaignMutation,
    useGetAdminV2GrowthCampaignMetricsQuery,
    useGetAdminV2GrowthSequencesQuery,
    useCreateAdminV2GrowthSequenceMutation,
    useGetAdminV2GrowthSequenceQuery,
    useUpdateAdminV2GrowthSequenceMutation,
    usePublishAdminV2GrowthSequenceMutation,
    useEnrollAdminV2GrowthSequencePersonMutation,
    useGetAdminV2GrowthSequencePersonEnrollmentsQuery,
    useUnenrollAdminV2GrowthSequenceEnrollmentMutation,
    useGetAdminV2AutomationsQuery,
    useCreateAdminV2AutomationMutation,
    useGetAdminV2AutomationQuery,
    useUpdateAdminV2AutomationDraftMutation,
    usePublishAdminV2AutomationMutation,
    useArchiveAdminV2AutomationMutation,
    useDryRunAdminV2AutomationMutation,
    useGetAdminV2AutomationRunsQuery,
    useGetAdminV2AutomationRunDetailQuery,
    useGetAdminV2SupportTicketsQuery,
    useCreateAdminV2SupportTicketMutation,
    useGetAdminV2SupportTicketQuery,
    useReplyAdminV2SupportTicketMutation,
    useAddAdminV2SupportTicketInternalNoteMutation,
    useAssignAdminV2SupportTicketMutation,
    useUpdateAdminV2SupportTicketStatusMutation,
    useGetAdminV2SupportTicketSuggestedArticlesMutation,
    useGetAdminV2HelpCollectionsQuery,
    useCreateAdminV2HelpCollectionMutation,
    useUpdateAdminV2HelpCollectionMutation,
    useGetAdminV2HelpArticlesQuery,
    useCreateAdminV2HelpArticleMutation,
    useGetAdminV2HelpArticleQuery,
    useUpdateAdminV2HelpArticleMutation,
    usePublishAdminV2HelpArticleMutation,
    useSearchAdminV2HelpArticlesMutation,
    useGetAdminV2HelpSearchLogsQuery,
    useGetAdminV2HelpFeedbackQuery,
    useCreateAdminV2HelpFeedbackMutation,
    useGetAdminPartnersQuery,
    useGetAdminPartnerQuery,
    useUpdateAdminPartnerEntitlementsMutation,
    useUpdateAdminPartnerDefaultOrganisationMutation,
    useTransferAdminPartnerClientsMutation,
    useUnlinkAdminPartnerClientMutation,
    useGetIndustryBreakdownQuery,
    // Stripe subscription management hooks
    useGetStripeSubscriptionsQuery,
    useGetStripeActiveProductsQuery,
    useCreateCheckoutSessionMutation,
    useCreateManualCheckoutLinkMutation,
    useCreatePortalSessionMutation,
    useGetSubscriptionStatisticsQuery,
    // New product-grouped hooks
    useGetProductsWithPricesQuery,
    useGetStripeDiscountsQuery,
    useSetPrimaryPriceMutation,
    useCreateProductCheckoutSessionMutation,
    // Subscription objects hook
    useGetStripeSubscriptionObjectsQuery,
    // Grant and revoke free subscription hooks
    useGrantFreeSubscriptionMutation,
    useRevokeFreeSubscriptionMutation,
    // Admin subscription audit + manual adjustments hooks
    useGetAdminSubscriptionLocalSnapshotQuery,
    useCreateAdminSubscriptionAuditRunMutation,
    useGetAdminSubscriptionLatestAuditRunQuery,
    useGetAdminSubscriptionAuditRunQuery,
    useReconcileAdminSubscriptionFromStripeMutation,
    useRebuildAdminSubscriptionSnapshotsMutation,
    useFixAdminSubscriptionMetadataMutation,
    useGetAdminSubscriptionStripeSummaryQuery,
    useLazyGetAdminSubscriptionStripeSummaryQuery,
    usePreviewAdminSubscriptionInvoiceMutation,
    useListAdminSubscriptionActionsQuery,
    useExecuteAdminSubscriptionActionMutation,
    // Admin users with claims hook
    useGetUsersWithClaimsQuery,
    // Remove claim from user hook
    useRemoveClaimFromUserMutation,
    // Disconnect organisation hook
    useDisconnectOrganisationMutation,
    // Usage analytics hook
    useGetUsageAnalyticsQuery,
    // Demo access management hooks
    useGetDemoAccessAssignmentsQuery,
    useGrantDemoAccessMutation,
    useRevokeDemoAccessMutation,
    useProcessTranscriptIntakeMutation,
    useGetTranscriptIntakeJobQuery,
    useCancelTranscriptIntakeJobMutation,
    useCreateTranscriptIntakeItemsMutation,
    useGetAdminDocsSearchQuery,
    useCreateAdminDocsShareLinkMutation,
    useGetAdminDocsShareLinksQuery,
    useRevokeAdminDocsShareLinkMutation,
} = apiSlice;
